<script>
  import { onMount, onDestroy } from 'svelte';
  import AgGridSvelte from 'ag-grid-svelte';
  import 'ag-grid-community/styles/ag-grid.css';
  import 'ag-grid-community/styles/ag-theme-alpine.css';
  import { API_BASE_URL, isAdminUser } from './config.js';
  import EmptyState from './components/common/EmptyState.svelte';
  import DataTable from './components/tables/DataTable.svelte';
  import Button from './components/common/Button.svelte';
  import ButtonGroup from './components/common/ButtonGroup.svelte';
  import ProjectActionsCell from './components/tables/ProjectActionsCell.svelte';
  import Navigation from './components/layout/Navigation.svelte';
  import ProjectDetailModal from './components/modals/ProjectDetailModal.svelte';
  
  export let setPage;

  // 공통 API 응답 처리 함수
  function handleApiResponse(response) {
    if (response.status === 401 || response.status === 403) {
      console.warn('인증 실패. 로그인 페이지로 이동합니다.');
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      setPage('login');
      return false;
    }
    return true;
  }
  
  // 상태 변수 추가
  let user = null; // 사용자 정보
  let isAdmin = false; // 관리자 여부
  let isCompany = false; // 기업 사용자 여부
  
  // 탭 관련 상태 변수
  let activeTab = 'register'; // 'register' 또는 'manage'
  
  // 관리탭 관련 상태 변수
  let projects = []; // 공고 목록 (전체)
  let filteredProjects = []; // 검색 필터링된 공고 목록
  let paginatedProjects = []; // 현재 페이지의 공고 목록
  let isLoadingProjects = false; // 공고 로딩 상태
  let selectedProjects = []; // 선택된 공고들
  let selectAll = false; // 전체 선택 상태
  let isDeletingProjects = false; // 삭제 중 상태
  let projectError = ""; // 관리탭 에러 메시지
  let isUpdatingStatus = false; // 상태 변경 중 상태
  let showDeletedProjects = false; // 삭제된 프로젝트 표시 여부
  let excludeExpiredProjects = true; // 마감된 프로젝트 제외 여부 (기본값: true)
  let searchQuery = ""; // 검색어
  
  // 프로젝트 상세정보 모달 상태 변수
  let showProjectDetailModal = false; // 프로젝트 상세정보 모달 표시 여부
  let selectedProjectForDetail = null; // 상세정보를 볼 프로젝트
  let gridApi;
  
  // 반응형 관련 변수 추가
  let isMobile = false;
  
  // 페이징 관련 상태 변수 (App.svelte 방식)
  let paginationPage = 1; // 현재 페이지
  let itemsPerPage = 10; // 페이지당 항목 수
  let totalPages = 1; // 전체 페이지 수
  let pagesToShow = 5; // 한 번에 표시할 페이지 번호 수
  
  // 모바일 기기 확인 함수
  function checkMobileDevice() {
    if (typeof window === 'undefined') return false;
    const ua = window.navigator.userAgent.toLowerCase();
    const keywords = ['android', 'iphone', 'ipad', 'ipod', 'windows phone', 'mobile'];
    return keywords.some(k => ua.includes(k)) || window.innerWidth < 768;
  }

  // 화면 크기 변경 핸들러
  function handleResize() {
    const prev = isMobile;
    isMobile = checkMobileDevice();
    if (gridApi && prev !== isMobile) {
      // 레이아웃 변화 시 그리드 높이 재계산
      gridApi.setDomLayout(isMobile ? 'autoHeight' : 'normal');
      gridApi.resetRowHeights();
      gridApi.setRowData(filteredProjects);
    }
  }

  // 모바일 카드 렌더러
  function projectCardRenderer(params) {
    const project = params.data;
    if (!project) return '';
    
    const statusLabels = {
      'active': '모집중',
      'deleted': '삭제됨',
      'open': '채용 시 마감',
      'lapse': '마감',
      'closed': '상시 채용'
    };
    
    const statusColors = {
      'active': '#22c55e',
      'inactive': '#ef4444',
      'pending': '#f59e0b',
      'deleted': '#6c757d',
      'open': '#3b82f6',
      'lapse': '#6b7280',
      'closed': '#8b5cf6'
    };
    
    const statusText = statusLabels[project.status] || '활성';
    const statusColor = statusColors[project.status] || '#6b7280';
    
    // 공고 마감일 처리
    let deadlineText = '-';
    let deadlineClass = '';
    if (project.deadline) {
      const deadline = new Date(project.deadline);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      deadline.setHours(0, 0, 0, 0);
      
      const diffTime = deadline.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      deadlineText = deadline.toLocaleDateString('ko-KR');
      
      if (diffDays < 0) {
        deadlineClass = 'deadline-expired';
      } else if (diffDays === 0) {
        deadlineClass = 'deadline-today';
      } else if (diffDays <= 3) {
        deadlineClass = 'deadline-soon';
      }
    }

    const isDeleted = project.status === 'deleted';
    const deletedTag = isDeleted ? '<div class="project-deleted-tag">삭제됨</div>' : '';
    
    const createdAt = project.created_at ? new Date(project.created_at).toLocaleDateString('ko-KR') : '-';
    
    // 관리자 비고 표시
    let adminNotesRow = '';
    if (project.admin_notes) {
      adminNotesRow += `<div class="card-row"><strong>비고:</strong> ${project.admin_notes}</div>`;
    }
    if (project.admin_remark) {
      adminNotesRow += `<div class="card-row"><strong>비고 2:</strong> ${project.admin_remark}</div>`;
    }

    return `
      <div class="mobile-project-card">
        <div class="card-header">
          <div class="checkbox-container">
            <input type="checkbox" class="mobile-checkbox" data-project-id="${project.id}" />
          </div>
          <div class="project-title" onclick="viewProjectDetailFromGrid(${project.id})">${project.title || '제목 없음'}</div>
          <div class="badges">
            ${deletedTag}
          </div>
        </div>
        <div class="card-body">
          <div class="card-row"><strong>회사명:</strong> ${project.company || '-'}</div>
          <div class="card-row"><strong>카테고리:</strong> ${project.category || '-'}</div>
          <div class="card-row">
            <strong>상태:</strong> 
            <span style="color: ${statusColor}; font-weight: 600;">${statusText}</span>
          </div>
          <div class="card-row ${deadlineClass}">
            <strong>공고 마감일:</strong> ${deadlineText}
          </div>
          <div class="card-row"><strong>등록일:</strong> ${createdAt}</div>
          ${adminNotesRow}
          <div class="card-actions">
            <button class="btn-edit mobile-action-btn" onclick="editProjectFromGrid(${project.id})">수정</button>
            <button class="btn-delete mobile-action-btn" onclick="deleteProjectFromGrid(${project.id})">삭제</button>
          </div>
        </div>
      </div>
    `;
  }

  // 데스크톱 AG-Grid 컬럼 정의 (관리자 전용)
  const desktopColumnDefs = [
    {
      headerName: '선택',
      checkboxSelection: true,
      headerCheckboxSelection: true,
      headerCheckboxSelectionCurrentPageOnly: true,
      width: 50,
      pinned: 'left',
      sortable: false,
      filter: false,
      resizable: false
    },
    {
      headerName: '제목',
      field: 'title',
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'startsWith', 'endsWith'],
        defaultOption: 'contains',
        suppressAndOrCondition: true
      },
      minWidth: 250,
      flex: 2,
      cellRenderer: (params) => {
        const title = params.value || '';
        return `<span style="color: black; cursor: pointer;">${title}</span>`;
      },
      onCellClicked: (params) => {
        viewProjectDetail(params.data);
      }
    },
    {
      headerName: '카테고리',
      field: 'category',
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'startsWith', 'endsWith'],
        defaultOption: 'contains',
        suppressAndOrCondition: true
      },
      width: 120
    },
    {
      headerName: '비고',
      field: 'admin_notes',
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'startsWith', 'endsWith'],
        defaultOption: 'contains',
        suppressAndOrCondition: true
      },
      width: 100
    },
    {
      headerName: '비고 2',
      field: 'admin_remark',
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'startsWith', 'endsWith'],
        defaultOption: 'contains',
        suppressAndOrCondition: true
      },
      minWidth: 150,
      flex: 1,
      width: 140
    },
    {
      headerName: '상태',
      field: 'status',
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'startsWith', 'endsWith'],
        defaultOption: 'contains',
        suppressAndOrCondition: true
      },
      width: 100,
      filterValueGetter: (params) => {
        const status = params.data.status;
        const statusLabels = {
          'active': '모집중',
          'deleted': '삭제됨',
          'open': '채용 시 마감',
          'lapse': '마감',
          'closed': '상시 채용',
          'inactive': '비활성',
          'pending': '대기중'
        };
        return statusLabels[status] || status;
      },
      cellRenderer: (params) => {
        const status = params.value;
        const statusLabels = {
          'active': '모집중',
          'deleted': '삭제됨',
          'open': '채용 시 마감',
          'lapse': '마감',
          'closed': '상시 채용',
          'inactive': '비활성',
          'pending': '대기중'
        };
        const statusColors = {
          'active': '#22c55e',
          'deleted': '#6c757d',
          'open': '#3b82f6',
          'lapse': '#6b7280',
          'closed': '#8b5cf6',
          'inactive': '#ef4444',
          'pending': '#f59e0b'
        };
        const color = statusColors[status] || '#6b7280';
        const label = statusLabels[status] || status;
        return `<span style="color: ${color}; font-weight: 600;">${label}</span>`;
      }
    },
    {
      headerName: '공고 마감일',
      field: 'deadline',
      sortable: true,
      filter: 'agDateColumnFilter',
      width: 130,
      cellRenderer: (params) => {
        if (!params.value) return '';
        const date = new Date(params.value);
        return date.toLocaleDateString('ko-KR');
      }
    },
    {
      headerName: '등록일',
      field: 'created_at',
      sortable: true,
      filter: 'agDateColumnFilter',
      width: 130,
      cellRenderer: (params) => {
        if (!params.value) return '';
        const date = new Date(params.value);
        return date.toLocaleDateString('ko-KR');
      }
    },
    {
      headerName: '고정',
      field: 'is_pinned',
      sortable: true,
      filter: false,
      width: 70,
      cellRenderer: (params) => {
        const projectId = params.data.id;
        const isPinned = params.data.is_pinned || false;
        return `
          <div class="pin-checkbox-container">
            <input 
              type="checkbox" 
              class="pin-checkbox" 
              data-project-id="${projectId}" 
              ${isPinned ? 'checked' : ''} 
              onchange="toggleProjectPin(${projectId}, this.checked)"
            />
          </div>
        `;
      }
    },
    {
      headerName: '작업',
      field: 'actions',
      sortable: false,
      filter: false,
      width: 110,
      cellRenderer: (params) => {
        const projectId = params.data.id;
        return `
          <div class="action-buttons">
            <button class="btn-edit" onclick="editProjectFromGrid(${projectId})">수정</button>
            <button class="btn-delete" onclick="deleteProjectFromGrid(${projectId})">삭제</button>
          </div>
        `;
      }
    }
  ];

  // 모바일 컬럼 정의
  const mobileColumnDefs = [
    {
      headerName: '프로젝트 정보',
      field: 'id',
      minWidth: 300,
      flex: 1,
      cellRenderer: 'projectCardRenderer',
      autoHeight: true,
      wrapText: true,
      sortable: false,
      editable: false,
      checkboxSelection: false // 모바일에서는 카드 내부 체크박스 사용
    }
  ];

  // 반응형 컬럼 정의
  $: projectColumnDefs = isMobile ? mobileColumnDefs : desktopColumnDefs;
  
  // 페이지네이션 설정
  let paginationPageSize = 100; // 기본값 100개
  
  // 선택된 프로젝트 개수에 따른 반응형 변수
  $: hasSelectedProjects = selectedProjects && selectedProjects.length > 0;
  $: console.log('hasSelectedProjects:', hasSelectedProjects, 'selectedProjects:', selectedProjects);
  
  // 드롭다운 상태
  let showStatusDropdown = false;
  
  // 마감일 변경 모달 관련 상태
  let showDeadlineModal = false;
  let newDeadline = "";
  let isUpdatingDeadline = false;
  
  // 그리드 옵션
  $: gridOptions = {
    domLayout: 'autoHeight',
    rowSelection: 'multiple',
    components: { projectCardRenderer },
    defaultColDef: { resizable: true },
    suppressRowClickSelection: true,
    suppressRowDeselection: false,
    getRowId: (params) => params.data.id,
    deltaSort: true,
    suppressClearOnFillReduction: true,
    // 체크박스 선택 강제 활성화
    rowMultiSelectWithClick: false,
    suppressCellSelection: true
  };

  // 모바일 체크박스 처리 함수
  function handleMobileCheckboxChange(event) {
    const checkbox = event.target;
    const projectId = parseInt(checkbox.dataset.projectId);
    const isChecked = checkbox.checked;
    
    if (isChecked) {
      // 체크된 경우: 해당 프로젝트를 선택 목록에 추가
      const project = filteredProjects.find(p => p.id === projectId);
      if (project && !selectedProjects.includes(projectId)) {
        selectedProjects = [...selectedProjects, projectId];
      }
    } else {
      // 체크 해제된 경우: 선택 목록에서 제거
      selectedProjects = selectedProjects.filter(id => id !== projectId);
    }
  }

  // 모바일 체크박스 이벤트 리스너 설정
  function setupMobileCheckboxListeners() {
    if (!isMobile) return;
    
    setTimeout(() => {
      const checkboxes = document.querySelectorAll('.mobile-checkbox');
      checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', handleMobileCheckboxChange);
      });
    }, 100);
  }

  // 그리드 데이터 변경 시 모바일 체크박스 리스너 재설정
  $: if (isMobile && filteredProjects) {
    setupMobileCheckboxListeners();
  }
  
  // AG-Grid 이벤트 핸들러
  function onGridReady(params) {
    gridApi = params.api;
    console.log('AG-Grid 준비 완료', params);
    console.log('gridApi:', gridApi);
    
    // AG-Grid가 준비된 후 저장된 필터 상태 복원
    setTimeout(() => {
      const savedState = localStorage.getItem('projectManageFilterState');
      if (savedState) {
        try {
          const filterState = JSON.parse(savedState);
          if (filterState.agGridFilterModel && gridApi && typeof gridApi.setFilterModel === 'function') {
            gridApi.setFilterModel(filterState.agGridFilterModel);
            console.log('🔄 AG-Grid 준비 후 필터 모델 복원:', filterState.agGridFilterModel);
          }
        } catch (error) {
          console.warn('AG-Grid 준비 후 필터 복원 실패:', error);
        }
      }
    }, 200);
    
    // 테스트용: 강제로 선택 이벤트 확인
    setTimeout(() => {
      console.log('Grid API 메서드들:', Object.keys(gridApi));
      console.log('선택된 행 수:', gridApi.getSelectedRows().length);
    }, 1000);

    // 폴링으로 선택 상태 확인 (임시 해결책)
    const checkSelection = () => {
      if (gridApi && typeof gridApi.getSelectedRows === 'function') {
        try {
          const selectedRows = gridApi.getSelectedRows();
          if (selectedRows && Array.isArray(selectedRows)) {
            const currentSelected = selectedRows.map(row => row.id);
            if (JSON.stringify(currentSelected) !== JSON.stringify(selectedProjects)) {
              console.log('폴링으로 선택 변경 감지:', currentSelected);
              selectedProjects = currentSelected;
            }
          }
        } catch (error) {
          console.warn('그리드 선택 상태 확인 중 오류:', error);
        }
      }
    };
    
    selectionPollingInterval = setInterval(checkSelection, 500);
    
    // 컴포넌트가 언마운트될 때 인터벌 정리
    setTimeout(() => {
      console.log('폴링 시작됨');
    }, 100);
  }

  function onSelectionChanged(event) {
    console.log('onSelectionChanged 호출됨', event);
    if (gridApi && typeof gridApi.getSelectedRows === 'function') {
      try {
        const selectedRows = gridApi.getSelectedRows();
        if (selectedRows && Array.isArray(selectedRows)) {
          console.log('선택된 행들:', selectedRows);
          const newSelectedProjects = selectedRows.map(row => row.id);
          console.log('선택된 프로젝트 ID들:', newSelectedProjects);
      
          // 명시적으로 배열을 새로 할당
          selectedProjects = newSelectedProjects;
          console.log('selectedProjects.length:', selectedProjects.length);
          console.log('hasSelectedProjects 계산:', selectedProjects.length > 0);
        }
      } catch (error) {
        console.warn('선택 변경 처리 중 오류:', error);
      }
    } else {
      console.log('gridApi가 없습니다');
    }
  }

  // 추가 이벤트 핸들러들
  function onRowClicked(event) {
    console.log('Row clicked:', event);
  }

  function onCellClicked(event) {
    console.log('Cell clicked:', event);
  }

  // AG-Grid 필터 변경 이벤트 핸들러
  function onFilterChanged(event) {
    console.log('Filter changed:', event);
    // 필터 상태 저장 (디바운싱을 위해 약간의 지연 추가)
    setTimeout(() => {
      saveFilterState();
    }, 500);
  }

  // 수동 선택 토글 함수 (테스트용)
  function toggleProjectSelection(projectId) {
    console.log('수동 선택 토글:', projectId);
    if (selectedProjects.includes(projectId)) {
      selectedProjects = selectedProjects.filter(id => id !== projectId);
    } else {
      selectedProjects = [...selectedProjects, projectId];
    }
    console.log('업데이트된 selectedProjects:', selectedProjects);
  }

  // 프로젝트 상세 보기 함수
  function viewProjectDetail(project) {
    selectedProjectForDetail = project;
    showProjectDetailModal = true;
  }

  // 글로벌 함수들 (AG-Grid 버튼 클릭 시 호출)
  if (typeof window !== 'undefined') {
    window.editProjectFromGrid = function(projectId) {
      const project = filteredProjects.find(p => p.id === projectId);
      if (project) {
        editProject(project);
      }
    };

    window.deleteProjectFromGrid = function(projectId) {
      const project = filteredProjects.find(p => p.id === projectId);
      if (project) {
        deleteProject(project);
      }
    };

    window.viewProjectDetailFromGrid = function(projectId) {
      const project = filteredProjects.find(p => p.id === projectId);
      if (project) {
        viewProjectDetail(project);
      }
    };

    window.toggleProjectPin = async function(projectId, isPinned) {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          alert('로그인이 필요합니다.');
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/pin`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ is_pinned: isPinned })
        });

        const data = await response.json();
        
        if (response.ok) {
          // 성공적으로 변경된 경우 목록 새로고침
          await loadProjects();
          console.log(`프로젝트 ${projectId} 상단 고정 상태가 ${isPinned ? '활성화' : '비활성화'}되었습니다.`);
        } else {
          alert(data.error || '상단 고정 상태 변경에 실패했습니다.');
          // 체크박스 상태 원복
          const checkbox = document.querySelector(`input[data-project-id="${projectId}"]`);
          if (checkbox) {
            checkbox.checked = !isPinned;
          }
        }
      } catch (error) {
        console.error('상단 고정 변경 오류:', error);
        alert('상단 고정 상태 변경 중 오류가 발생했습니다.');
        // 체크박스 상태 원복
        const checkbox = document.querySelector(`input[data-project-id="${projectId}"]`);
        if (checkbox) {
          checkbox.checked = !isPinned;
        }
      }
    };
  }
  
  // 폴링 인터벌 ID 저장용
  let selectionPollingInterval = null;

  // 드롭다운 외부 클릭 시 닫기
  function handleClickOutside(event) {
    if (showStatusDropdown && !event.target.closest('.dropdown')) {
      showStatusDropdown = false;
    }
  }
  
  // 마감일 변경 모달 열기 함수
  function openDeadlineModal() {
    showStatusDropdown = false;
    newDeadline = getTodayString(); // 오늘 날짜로 초기화
    showDeadlineModal = true;
  }
  
  // 마감일 변경 모달 닫기 함수
  function closeDeadlineModal() {
    showDeadlineModal = false;
    newDeadline = "";
  }
  
  // 마감일 일괄 변경 함수
  async function updateProjectsDeadline() {
    if (selectedProjects.length === 0) {
      projectError = "마감일을 변경할 공고를 선택해주세요.";
      return;
    }
    
    if (!newDeadline) {
      projectError = "새로운 마감일을 선택해주세요.";
      return;
    }
    
    const deadlineDate = new Date(newDeadline);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    deadlineDate.setHours(0, 0, 0, 0);
    
    if (deadlineDate < today) {
      projectError = "마감일은 오늘 이후 날짜로 설정해주세요.";
      return;
    }
    
    // 재확인 없이 즉시 변경
    
    try {
      isUpdatingDeadline = true;
      projectError = "";
      
      const token = localStorage.getItem('token');
      if (!token) {
        projectError = "로그인이 필요합니다.";
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/projects/bulk-deadline`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          projectIds: selectedProjects,
          deadline: newDeadline
        })
      });
      
      if (!response.ok) {
        if (!handleApiResponse(response)) return;
      }
      
      const data = await response.json();
      
      if (response.ok) {
        // 성공적으로 마감일 변경된 경우 목록 새로고침
        await loadProjects();
        closeDeadlineModal();
      } else {
        projectError = data.error || `공고 마감일 변경에 실패했습니다.`;
      }
    } catch (error) {
      console.error('마감일 변경 오류:', error);
      projectError = `마감일 변경 오류: ${error.message}`;
    } finally {
      isUpdatingDeadline = false;
    }
  }

  // 컴포넌트 마운트 시 사용자 정보 로드
  onMount(() => {
    // 토큰 체크
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('토큰이 없습니다. 로그인 페이지로 이동합니다.');
      setPage('login');
      return;
    }

    // 모바일 기기 확인
    isMobile = checkMobileDevice();
    window.addEventListener('resize', handleResize);
    document.addEventListener('click', handleClickOutside);
    
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        user = JSON.parse(storedUser);
        // 디버깅을 위한 콘솔 로그
        console.log('사용자 정보:', user);
        console.log('사용자 역할:', user.role);
        console.log('사용자 이메일:', user.email);
        // 사용자 역할 확인
        isAdmin = isAdminUser(user);
        console.log('관리자 여부:', isAdmin);
        // 기업 사용자 확인
        isCompany = user.role === 'company';
      } catch (error) {
        console.error('사용자 정보 파싱 오류:', error);
      }
    }

    // localStorage에서 활성 탭 상태 확인
    const savedActiveTab = localStorage.getItem('projectRegisterActiveTab');
    if (savedActiveTab) {
      activeTab = savedActiveTab;
      // 사용 후 localStorage에서 제거
      localStorage.removeItem('projectRegisterActiveTab');
      
      // 관리 탭으로 이동하는 경우 필터링 상태 복원 및 프로젝트 목록 로드
      if (activeTab === 'manage') {
        loadFilterState(); // 필터링 상태 복원
        loadProjects();
      }
    }

    // 초기 날짜 기본값 설정
    setDefaultDates();
    
    // 지역 데이터 초기 로딩
    loadCities();
    
    // 수정 모드인 경우 기존 지역 정보 복원 (지역 데이터 로딩 완료 후)
    if (processedData && (projectData.code_value || projectData.location)) {
      setTimeout(() => {
        prefillRegionFromCode(projectData.code_value || projectData.location);
      }, 500); // 시/도 데이터 로딩 대기
    }

    // 테이블 액션 이벤트 리스너 추가
    const handleEdit = (event) => editProject(event.detail);
    const handleDelete = (event) => deleteProject(event.detail);
    
    document.addEventListener('edit', handleEdit);
    document.addEventListener('delete', handleDelete);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('edit', handleEdit);
      document.removeEventListener('delete', handleDelete);
      document.removeEventListener('click', handleClickOutside);
      
      // 폴링 인터벌 정리
      if (selectionPollingInterval) {
        clearInterval(selectionPollingInterval);
        console.log('폴링 인터벌 정리됨');
      }
    };
  });

  onDestroy(() => {
    // 폴링 인터벌 정리
    if (selectionPollingInterval) {
      clearInterval(selectionPollingInterval);
      selectionPollingInterval = null;
    }
    // 그리드 API 참조 정리
    gridApi = null;
  });
  
  // 탭 변경 함수
  function switchTab(tab) {
    activeTab = tab;
    if (tab === 'manage') {
      loadFilterState(); // 필터링 상태 복원
      loadProjects();
    }
  }
  
  // 공고 목록 로드 함수 (App.svelte 방식으로 변경)
  async function loadProjects() {
    try {
      isLoadingProjects = true;
      projectError = "";
      
      const token = localStorage.getItem('token');
      if (!token) {
        projectError = "로그인이 필요합니다.";
        return;
      }
      
      // 먼저 마감일 지난 프로젝트들을 자동으로 업데이트
      try {
        await updateExpiredProjects();
      } catch (error) {
        console.warn('마감일 지난 프로젝트 자동 업데이트 실패:', error);
        // 자동 업데이트 실패해도 목록 로드는 계속 진행
      }
      
      const url = `${API_BASE_URL}/api/projects${showDeletedProjects ? '?includeDeleted=true' : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        projects = data.projects || data || [];
        
        // 검색 필터링 적용
        applySearchFilter();
        
        // 선택 상태 초기화
        selectedProjects = [];
        selectAll = false;
        
        // 페이지네이션 업데이트
        paginationPage = 1;
        updatePaginatedProjects();
        
        // AG-Grid 필터 상태 복원 (데이터 로드 후)
        setTimeout(() => {
          const savedState = localStorage.getItem('projectManageFilterState');
          if (savedState && gridApi && typeof gridApi.setFilterModel === 'function') {
            try {
              const filterState = JSON.parse(savedState);
              if (filterState.agGridFilterModel) {
                gridApi.setFilterModel(filterState.agGridFilterModel);
                console.log('🔄 데이터 로드 후 AG-Grid 필터 복원:', filterState.agGridFilterModel);
              }
            } catch (error) {
              console.warn('데이터 로드 후 AG-Grid 필터 복원 실패:', error);
            }
          }
        }, 300);
      } else {
        projectError = data.error || "공고 목록을 불러오는데 실패했습니다.";
      }
    } catch (error) {
      console.error('공고 목록 로드 오류:', error);
      projectError = `오류: ${error.message}`;
    } finally {
      isLoadingProjects = false;
    }
  }

  // 마감일 지난 프로젝트들을 자동으로 '마감' 상태로 업데이트하는 함수
  async function updateExpiredProjects() {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('로그인이 필요합니다.');
    }

    const response = await fetch(`${API_BASE_URL}/api/projects/update-expired`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({}) // 빈 JSON 객체 전송
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || '마감일 지난 프로젝트 업데이트에 실패했습니다.');
    }

    const data = await response.json();
    console.log(`마감일 자동 업데이트: ${data.updatedCount}개의 프로젝트가 '마감' 상태로 변경됨`);
    return data;
  }
  
  // 필터링 상태를 localStorage에 저장하는 함수
  function saveFilterState() {
    const filterState = {
      showDeletedProjects,
      excludeExpiredProjects,
      searchQuery,
      agGridFilterModel: null, // AG-Grid 필터 모델 저장
      timestamp: new Date().toISOString()
    };
    
    // AG-Grid 필터 상태도 함께 저장
    if (gridApi && typeof gridApi.getFilterModel === 'function') {
      try {
        filterState.agGridFilterModel = gridApi.getFilterModel();
        console.log('🔖 AG-Grid 필터 모델 저장:', filterState.agGridFilterModel);
      } catch (error) {
        console.warn('AG-Grid 필터 모델 저장 실패:', error);
      }
    }
    
    localStorage.setItem('projectManageFilterState', JSON.stringify(filterState));
    console.log('🔖 필터링 상태 저장:', filterState);
  }
  
  // localStorage에서 필터링 상태를 복원하는 함수
  function loadFilterState() {
    try {
      const savedState = localStorage.getItem('projectManageFilterState');
      if (savedState) {
        const filterState = JSON.parse(savedState);
        showDeletedProjects = filterState.showDeletedProjects || false;
        excludeExpiredProjects = filterState.excludeExpiredProjects !== undefined ? filterState.excludeExpiredProjects : true;
        searchQuery = filterState.searchQuery || "";
        
        // AG-Grid 필터 상태 복원 (gridApi가 준비된 후에 실행)
        if (filterState.agGridFilterModel) {
          // gridApi가 준비될 때까지 기다린 후 필터 적용
          const restoreAgGridFilter = () => {
            if (gridApi && typeof gridApi.setFilterModel === 'function') {
              try {
                gridApi.setFilterModel(filterState.agGridFilterModel);
                console.log('🔄 AG-Grid 필터 모델 복원:', filterState.agGridFilterModel);
              } catch (error) {
                console.warn('AG-Grid 필터 모델 복원 실패:', error);
              }
            } else {
              // gridApi가 아직 준비되지 않았다면 조금 더 기다림
              setTimeout(restoreAgGridFilter, 100);
            }
          };
          
          // 즉시 시도하고, 실패하면 지연 실행
          restoreAgGridFilter();
        }
        
        console.log('🔄 필터링 상태 복원:', filterState);
        return true;
      }
    } catch (error) {
      console.error('필터링 상태 복원 오류:', error);
    }
    return false;
  }
  
  // 로그아웃 시 필터링 상태 초기화 함수 (필요시 호출)
  function clearFilterState() {
    localStorage.removeItem('projectManageFilterState');
    console.log('🗑️ 필터링 상태 초기화');
  }

  // 모든 필터링 조건 초기화 함수
  function resetAllFilters() {
    console.log('🔄 모든 필터링 조건 초기화 시작');
    
    // 1. 검색어 초기화
    searchQuery = "";
    
    // 2. 체크박스 필터 초기화
    showDeletedProjects = false;
    excludeExpiredProjects = true;
    
    // 3. AG-Grid 필터 초기화
    if (gridApi && typeof gridApi.setFilterModel === 'function') {
      try {
        gridApi.setFilterModel(null);
        console.log('✅ AG-Grid 필터 초기화 완료');
      } catch (error) {
        console.warn('AG-Grid 필터 초기화 실패:', error);
      }
    }
    
    // 4. localStorage에서 필터링 상태 제거
    localStorage.removeItem('projectManageFilterState');
    
    // 5. 필터 적용 및 목록 새로고침
    applySearchFilter();
    updatePaginatedProjects();
    
    console.log('✅ 모든 필터링 조건 초기화 완료');
  }
  
  // 검색 및 필터링 함수
  function applySearchFilter() {
    let baseProjects = [...projects];
    
    // 1. 삭제된 프로젝트 필터링
    if (!showDeletedProjects) {
      baseProjects = baseProjects.filter(project => !project.is_deleted);
    }
    
    // 2. 마감된 프로젝트 필터링
    if (excludeExpiredProjects) {
      baseProjects = baseProjects.filter(project => project.status !== 'lapse');
    }
    
    // 3. 검색어 필터링
    if (!searchQuery.trim()) {
      filteredProjects = baseProjects;
    } else {
      const query = searchQuery.toLowerCase().trim();
      filteredProjects = baseProjects.filter(project => {
        const searchFields = [
          project.title,
          project.company,
          project.category,
          project.description,
          project.deadline // 공고 마감일도 검색 대상에 포함
        ];
        
        // admin_notes와 admin_remark도 검색 대상에 포함
        if (project.admin_notes) {
          searchFields.push(project.admin_notes);
        }
        if (project.admin_remark) {
          searchFields.push(project.admin_remark);
        }
        
        return searchFields.some(field => 
          field && field.toLowerCase().includes(query)
        );
      });
    }
    
    console.log('🔍 검색 및 필터링 완료:', {
      searchQuery: searchQuery,
      totalProjects: projects.length,
      filteredProjects: filteredProjects.length,
      excludeExpired: excludeExpiredProjects,
      showDeleted: showDeletedProjects,
      timestamp: new Date().toISOString()
    });
  }

  // 검색어 변경 시 호출되는 함수
  function handleSearchChange() {
    applySearchFilter();
    paginationPage = 1; // 검색 시 첫 페이지로 이동
    updatePaginatedProjects();
    saveFilterState(); // 필터링 상태 저장
  }

  // 검색 초기화 함수
  function clearSearch() {
    searchQuery = "";
    handleSearchChange();
  }

  // 페이지네이션 업데이트 함수 (검색 결과 기준으로 수정)
  function updatePaginatedProjects() {
    const oldPaginationPage = paginationPage;
    const oldTotalPages = totalPages;
    
    if (filteredProjects.length === 0) {
      paginatedProjects = [];
      totalPages = 1;
      paginationPage = 1;
      console.log('📝 페이지네이션 업데이트 (빈 데이터):', {
        totalProjects: 0,
        totalPages: 1,
        currentPage: 1,
        timestamp: new Date().toISOString()
      });
      return;
    }
    
    // 전체 페이지 수 계산 (필터링된 결과 기준)
    totalPages = Math.ceil(filteredProjects.length / itemsPerPage);
    
    // 현재 페이지가 유효한 범위 내에 있도록 함
    if (paginationPage > totalPages) {
      paginationPage = totalPages;
    } else if (paginationPage < 1) {
      paginationPage = 1;
    }
    
    // 현재 페이지에 해당하는 데이터 추출 (필터링된 결과에서)
    const startIndex = (paginationPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, filteredProjects.length);
    paginatedProjects = filteredProjects.slice(startIndex, endIndex);
    
    console.log('📄 페이지네이션 업데이트 완료:', {
      totalProjects: projects.length,
      filteredProjects: filteredProjects.length,
      itemsPerPage: itemsPerPage,
      oldTotalPages: oldTotalPages,
      newTotalPages: totalPages,
      oldCurrentPage: oldPaginationPage,
      newCurrentPage: paginationPage,
      startIndex: startIndex + 1,
      endIndex: endIndex,
      displayedItems: paginatedProjects.length,
      selectedProjects: selectedProjects.length,
      pageChanged: oldPaginationPage !== paginationPage,
      timestamp: new Date().toISOString()
    });
  }
  
  // 페이지 이동 함수들 (App.svelte에서 가져옴)
  function goToPage(page) {
    if (page >= 1 && page <= totalPages) {
      const previousPage = paginationPage;
      
      paginationPage = page;
      updatePaginatedProjects();
      
      console.log('🔄 페이지 이동 완료:', {
        previousPage: previousPage,
        newPage: page,
        currentPage: paginationPage,
        totalPages: totalPages,
        totalProjects: projects.length,
        itemsPerPage: itemsPerPage,
        displayedItems: paginatedProjects.length,
        startIndex: (paginationPage - 1) * itemsPerPage + 1,
        endIndex: Math.min(paginationPage * itemsPerPage, filteredProjects.length),
        timestamp: new Date().toISOString()
      });
    }
  }
  
  function goToPrevPage() {
    if (paginationPage > 1) {
      const targetPage = paginationPage - 1;
      console.log('⬅️ 이전 페이지 이동 시작:', {
        currentPage: paginationPage,
        targetPage: targetPage,
        timestamp: new Date().toISOString()
      });
      
      goToPage(targetPage);
    }
  }
  
  function goToNextPage() {
    if (paginationPage < totalPages) {
      const targetPage = paginationPage + 1;
      console.log('➡️ 다음 페이지 이동 시작:', {
        currentPage: paginationPage,
        targetPage: targetPage,
        timestamp: new Date().toISOString()
      });
      
      goToPage(targetPage);
    }
  }
  
  // 페이지네이션에 표시할 페이지 번호 배열 생성 함수 (반응형으로 변경)
  $: pageNumbers = (() => {
    console.log('📊 페이지 번호 계산:', {
      totalPages,
      paginationPage,
      pagesToShow,
      timestamp: new Date().toISOString()
    });
    
    if (totalPages <= 0) {
      return [];
    }
    
    if (totalPages <= pagesToShow) {
      const result = Array.from({ length: totalPages }, (_, i) => i + 1);
      console.log('📊 페이지 번호 결과 (전체 표시):', result);
      return result;
    }
    
    let startPage = Math.max(1, paginationPage - Math.floor(pagesToShow / 2));
    let endPage = startPage + pagesToShow - 1;
    
    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - pagesToShow + 1);
    }
    
    startPage = Math.max(1, startPage);
    
    const result = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
    console.log('📊 페이지 번호 결과 (부분 표시):', {
      startPage,
      endPage,
      result,
      totalPages
    });
    return result;
  })();
  
  // 페이지 크기 변경 함수
  function changePageSize(newSize) {
    const previousSize = itemsPerPage;
    const previousPage = paginationPage;
    const previousTotalPages = totalPages;
    
    console.log('📊 페이지당 항목 수 변경 시작:', {
      previousSize: previousSize,
      newSize: newSize,
      previousPage: previousPage,
      totalProjects: projects.length,
      filteredProjects: filteredProjects.length,
      previousTotalPages: previousTotalPages,
      newTotalPages: Math.ceil(filteredProjects.length / newSize),
      timestamp: new Date().toISOString()
    });
    
    itemsPerPage = newSize;
    paginationPage = 1; // 첫 페이지로 리셋
    updatePaginatedProjects();
    
    console.log('✅ 페이지당 항목 수 변경 완료:', {
      previousSize: previousSize,
      currentItemsPerPage: itemsPerPage,
      previousPage: previousPage,
      currentPage: paginationPage,
      previousTotalPages: previousTotalPages,
      newTotalPages: totalPages,
      displayedItems: paginatedProjects.length,
      startIndex: (paginationPage - 1) * itemsPerPage + 1,
      endIndex: Math.min(paginationPage * itemsPerPage, filteredProjects.length),
      timestamp: new Date().toISOString()
    });
  }
  
  // 전체 선택/해제 함수
  function toggleSelectAll() {
    if (selectAll) {
      selectedProjects = projects.map(project => project.id);
    } else {
      selectedProjects = [];
    }
  }
  
  // 개별 선택 함수
  function toggleSelectProject(projectId) {
    if (selectedProjects.includes(projectId)) {
      selectedProjects = selectedProjects.filter(id => id !== projectId);
    } else {
      selectedProjects = [...selectedProjects, projectId];
    }
    
    // 전체 선택 상태 업데이트
    selectAll = selectedProjects.length === projects.length;
  }

  // 테이블 이벤트 핸들러
  function handleTableSelectAll({ detail }) {
    const { selected } = detail;
    if (selected) {
      selectedProjects = paginatedProjects.map(project => project.id);
    } else {
      selectedProjects = [];
    }
    selectAll = selected;
  }

  function handleTableSelect({ detail }) {
    const { row, selected } = detail;
    if (selected) {
      selectedProjects = [...selectedProjects, row.id];
    } else {
      selectedProjects = selectedProjects.filter(id => id !== row.id);
    }
    selectAll = selectedProjects.length === paginatedProjects.length;
  }

  function handleCellClick({ detail }) {
    const { row, column } = detail;
    if (column.field === 'title') {
      showProjectDetail(row);
    }
  }

  function deleteProject(project) {
    selectedProjects = [project.id];
    deleteProjects();
  }

  // 프로젝트 데이터를 테이블에 맞게 포맷
  $: formattedProjects = paginatedProjects.map(project => {
    const statusLabels = {
      'active': '모집중',
      'deleted': '삭제됨',
      'open': '채용 시 마감',
      'lapse': '마감',
      'closed': '상시 채용'
    };
    
    // 공고 마감일 상태 계산
    let deadlineClass = '';
    let deadlineDisplay = '-';
    
    if (project.deadline) {
      const deadline = new Date(project.deadline);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      deadline.setHours(0, 0, 0, 0);
      
      const diffTime = deadline.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      deadlineDisplay = deadline.toLocaleDateString('ko-KR');
      
      if (diffDays < 0) {
        deadlineClass = 'deadline-expired';
      } else if (diffDays === 0) {
        deadlineClass = 'deadline-today';
      } else if (diffDays <= 3) {
        deadlineClass = 'deadline-soon';
      }
    }
    
    const baseData = {
      ...project,
      title: project.title || '제목 없음',
      company: project.company || '-',
      category: project.category || '-',
      status: statusLabels[project.status] || '활성',
      deadline: deadlineDisplay,
      created_at: project.created_at ? new Date(project.created_at).toLocaleDateString('ko-KR') : '-',
      actions: project, // 액션 버튼을 위해 전체 프로젝트 객체 전달
      _isDeleted: project.status === 'deleted', // 삭제된 항목 표시를 위한 플래그
      className: project.status === 'deleted' ? 'deleted-row' : '', // 삭제된 행에 CSS 클래스 추가
      _deadlineClass: deadlineClass // 마감일 상태 클래스
    };

    // admin_notes와 admin_remark 추가
    baseData.admin_notes = project.admin_notes || '-';
    baseData.admin_remark = project.admin_remark || '-';

    return baseData;
  });
  
  // 공고 삭제 함수
  async function deleteProjects() {
    if (selectedProjects.length === 0) {
      projectError = "삭제할 공고를 선택해주세요.";
      return;
    }
    
    if (!confirm(`선택한 ${selectedProjects.length}개의 공고를 삭제하시겠습니까?`)) {
      return;
    }
    
    try {
      isDeletingProjects = true;
      projectError = "";
      
      const token = localStorage.getItem('token');
      if (!token) {
        projectError = "로그인이 필요합니다.";
        return;
      }
      
      // bulk-delete API 시도
      console.log('일괄 삭제 시도:', selectedProjects);
      const response = await fetch(`${API_BASE_URL}/api/projects/bulk-delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ projectIds: selectedProjects })
      });
      
      if (!response.ok) {
        if (!handleApiResponse(response)) return;
      }
      
      const data = await response.json();
      console.log('일괄 삭제 응답:', response.status, data);
      
      if (response.ok) {
        // 성공적으로 삭제된 경우 목록 새로고침
        await loadProjects();
        alert(data.message || `${data.deletedCount || selectedProjects.length}개의 공고가 삭제되었습니다.`);
      } else {
        // bulk-delete가 실패한 경우 개별 삭제로 fallback
        console.warn('일괄 삭제 실패, 개별 삭제로 전환:', data);
        await deleteProjectsIndividually();
      }
    } catch (error) {
      console.error('일괄 삭제 오류:', error);
      // 네트워크 오류 등의 경우 개별 삭제로 fallback
      console.warn('일괄 삭제 실패, 개별 삭제로 전환');
      await deleteProjectsIndividually();
    } finally {
      isDeletingProjects = false;
    }
  }
  
  // 개별 삭제 함수 (fallback)
  async function deleteProjectsIndividually() {
    try {
      const token = localStorage.getItem('token');
      let deletedCount = 0;
      let failedCount = 0;
      
      for (const projectId of selectedProjects) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (!response.ok) {
            if (!handleApiResponse(response)) return;
          }
          
          if (response.ok) {
            deletedCount++;
          } else {
            failedCount++;
            console.error(`프로젝트 ${projectId} 삭제 실패:`, await response.text());
          }
        } catch (error) {
          failedCount++;
          console.error(`프로젝트 ${projectId} 삭제 오류:`, error);
        }
      }
      
      // 결과 표시
      if (deletedCount > 0) {
        await loadProjects();
        if (failedCount > 0) {
          alert(`${deletedCount}개 삭제 완료, ${failedCount}개 삭제 실패`);
          projectError = `일부 공고 삭제에 실패했습니다. (성공: ${deletedCount}, 실패: ${failedCount})`;
        } else {
          alert(`${deletedCount}개의 공고가 삭제되었습니다.`);
        }
      } else {
        projectError = "공고 삭제에 실패했습니다.";
      }
    } catch (error) {
      console.error('개별 삭제 오류:', error);
      projectError = `삭제 오류: ${error.message}`;
    }
  }
  
  // 프로젝트 일괄 상태 변경 함수
  async function updateProjectsStatus(status, statusLabel) {
    if (selectedProjects.length === 0) {
      projectError = "상태를 변경할 공고를 선택해주세요.";
      return;
    }
    
    if (!confirm(`선택한 ${selectedProjects.length}개의 공고를 '${statusLabel}' 상태로 변경하시겠습니까?`)) {
      return;
    }
    
    try {
      isUpdatingStatus = true;
      projectError = "";
      
      const token = localStorage.getItem('token');
      if (!token) {
        projectError = "로그인이 필요합니다.";
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/projects/bulk-status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          projectIds: selectedProjects,
          status: status
        })
      });
      
      if (!response.ok) {
        if (!handleApiResponse(response)) return;
      }
      
      const data = await response.json();
      
      if (response.ok) {
        // 성공적으로 상태 변경된 경우 목록 새로고침
        await loadProjects();
        alert(data.message || `${data.updatedCount || selectedProjects.length}개의 공고가 ${statusLabel} 상태로 변경되었습니다.`);
      } else {
        projectError = data.error || `공고 상태 변경에 실패했습니다.`;
      }
    } catch (error) {
      console.error('상태 변경 오류:', error);
      projectError = `상태 변경 오류: ${error.message}`;
    } finally {
      isUpdatingStatus = false;
    }
  }

  // 마감 처리 함수
  function closeProjects() {
    updateProjectsStatus('closed', '상시 채용');
  }

  // 활성화 처리 함수
  function activateProjects() {
    updateProjectsStatus('active', '모집중');
  }

  // 채용 시 마감으로 변경하는 함수
  function setAutoCloseProjects() {
    updateProjectsStatus('open', '채용 시 마감');
  }

  // 마감 처리 함수 (lapse 상태)
  function lapseProjects() {
    updateProjectsStatus('lapse', '마감');
  }

  // 드롭다운 상태 변경 핸들러
  function handleStatusChange(action) {
    showStatusDropdown = false;
    
    switch(action) {
      case 'active':
        activateProjects();
        break;
      case 'open':
        setAutoCloseProjects();
        break;
      case 'lapse':
        lapseProjects();
        break;
      case 'closed':
        closeProjects();
        break;
      case 'deadline':
        openDeadlineModal();
        break;
      case 'delete':
        deleteProjects();
        break;
    }
  }
  
  
  // API 키는 서버에서 동적으로 가져옴
  let API_KEY = null;
  const MODEL = "gemini-2.5-flash";
  
  // 상태 변수
  let originalText = "";
  let isProcessing = false;
  let processedData = null;
  let errorMessage = "";
  let isRegistered = false;
  let isManualRegistration = false; // 수동 등록 모드 여부
  
  // 프로젝트 표준화된 형식으로 저장할 객체 (기본값으로 오늘과 5일 뒤 설정)
  let projectData = {
    title: "",
    company: "",
    skills: [],
    requirements: [],
    preferredSkills: [],
    level: "",
    headcount: "",
    location: "", // 사용자 입력 주소 텍스트 (예: "서울시 강남구 테헤란로 123")
    code_value: "", // 지역 드롭다운에서 선택된 지역 코드 (예: SU001)
    salary: "",
    startDate: "", // 빈 문자열로 초기화, 기본값은 setDefaultDates()에서 설정
    endDate: "", // 빈 문자열로 초기화, 기본값은 setDefaultDates()에서 설정
    deadline: "", // 공고 마감일 - 빈 문자열로 초기화, 기본값은 setDefaultDates()에서 설정
    description: "",
    contact: "",
    category: "",
    status: "active", // 기본값: 모집중
    admin_notes: "", // 관리자 전용 비고
    admin_remark: "" // 관리자 전용 비고 2
  };
  
  // 지역 선택 관련 상태 변수
  let cities = []; // 시/도 목록
  let selectedCity = ""; // 선택된 시/도 코드
  let isLoadingCities = false; // 시/도 로딩 상태
  let regionError = ""; // 지역 선택 에러 메시지
  
  // 카테고리 선택 옵션
  const categories = [
    "웹 개발",
    "앱 개발",
    "서버/백엔드",
    "프론트엔드",
    "데이터베이스",
    "DevOps",
    "AI/머신러닝",
    "보안",
    "UI/UX",
    "기타"
  ];

  // 상태 옵션 정의
  const statusOptions = [
    { value: "active", label: "모집중", description: "현재 모집중인 공고" },
    { value: "open", label: "채용 시 마감", description: "채용 완료 시 자동으로 마감되는 공고" },
    { value: "closed", label: "상시 채용", description: "상시적으로 인재를 모집하는 공고" }
  ];
  
  // 오늘 날짜를 YYYY-MM-DD 형식으로 반환하는 헬퍼 함수
  function getTodayString() {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }

  // N일 뒤 날짜를 YYYY-MM-DD 형식으로 반환하는 헬퍼 함수
  function getDateAfterDays(days) {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  }

  // 날짜 기본값 설정 함수 (간단하고 명확한 버전)
  function setDefaultDates() {
    console.log('🗓️ 날짜 기본값 설정 시작:', {
      currentStartDate: projectData.startDate,
      currentEndDate: projectData.endDate,
      currentDeadline: projectData.deadline,
      timestamp: new Date().toISOString()
    });

    // 1. 시작일이 비어있다면 오늘 날짜로 설정
    if (!projectData.startDate || projectData.startDate.trim() === '') {
      projectData.startDate = getTodayString();
      console.log('✅ 시작일을 오늘 날짜로 설정:', projectData.startDate);
    }
    
    // 2. 종료일이 비어있다면 시작일보다 5일 뒤로 설정
    if (!projectData.endDate || projectData.endDate.trim() === '') {
      const startDate = new Date(projectData.startDate);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 5);
      projectData.endDate = endDate.toISOString().split('T')[0];
      console.log('✅ 종료일을 시작일+5일로 설정:', projectData.endDate);
    }

    // 3. 공고 마감일이 비어있다면 오늘 날짜에서 5일 뒤로 설정
    if (!projectData.deadline || projectData.deadline.trim() === '') {
      const today = new Date();
      const deadline = new Date(today);
      deadline.setDate(today.getDate() + 5);
      projectData.deadline = deadline.toISOString().split('T')[0];
      console.log('✅ 공고 마감일을 오늘+5일로 설정:', projectData.deadline);
    }

    console.log('🗓️ 날짜 기본값 설정 완료:', {
      finalStartDate: projectData.startDate,
      finalEndDate: projectData.endDate,
      finalDeadline: projectData.deadline,
      timestamp: new Date().toISOString()
    });
  }

  // 시작일 변경 시 호출되는 함수
  function handleStartDateChange() {
    console.log('📅 시작일 변경됨:', projectData.startDate);
    
    // 종료일이 비어있거나 시작일보다 이전이면 자동으로 5일 뒤로 설정
    if (!projectData.endDate || projectData.endDate.trim() === '' || 
        new Date(projectData.endDate) <= new Date(projectData.startDate)) {
      if (projectData.startDate && projectData.startDate.trim() !== '') {
        const startDate = new Date(projectData.startDate);
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 5);
        projectData.endDate = endDate.toISOString().split('T')[0];
        console.log('✅ 종료일 자동 조정:', projectData.endDate);
      }
    }
  }

  // 지역 데이터 로딩 함수들
  
  // 시/도 목록 로딩
  async function loadCities() {
    try {
      isLoadingCities = true;
      regionError = "";
      
      const response = await fetch(`${API_BASE_URL}/api/codes/regions/cities`);
      const data = await response.json();
      
      if (response.ok && data.success) {
        cities = data.data || [];
        console.log('시/도 목록 로딩 완료:', cities.length, '개');
      } else {
        regionError = data.message || "시/도 목록을 불러오는데 실패했습니다.";
      }
    } catch (error) {
      console.error('시/도 목록 로딩 오류:', error);
      regionError = `시/도 목록 로딩 오류: ${error.message}`;
    } finally {
      isLoadingCities = false;
    }
  }
  
  
  // 시/도 선택 핸들러
  function handleCityChange() {
    console.log('시/도 선택 변경:', selectedCity);
    projectData.code_value = selectedCity; // 지역 코드
  }
  
  // 저장된 지역 코드로부터 시/도 정보를 복원하는 함수
  async function prefillRegionFromCode(locationCode) {
    if (!locationCode) return;
    
    try {
      // 먼저 시/도 목록이 로딩되어 있는지 확인
      if (cities.length === 0) {
        await loadCities();
      }
      
      // 시/도 코드인 경우 또는 구/군 코드에서 시/도 부분만 추출
      if (locationCode.length === 5) {
        if (locationCode.endsWith('000')) {
          // 시/도 코드인 경우
          selectedCity = locationCode;
        } else {
          // 구/군 코드인 경우 - 해당하는 시/도 코드로 변환
          const cityPrefix = locationCode.substring(0, 2) + '000';
          const city = cities.find(c => c.code_value === cityPrefix);
          if (city) {
            selectedCity = cityPrefix;
          }
        }
      }
    } catch (error) {
      console.error('지역 정보 복원 오류:', error);
    }
  }
  
  // 지역명을 지역 코드로 매핑하는 함수
  function mapRegionNameToCode(regionName) {
    if (!regionName) return '';
    
    const regionMapping = {
      '서울': 'SU000',
      '서울시': 'SU000',
      '서울특별시': 'SU000',
      '부산': 'BS000',
      '부산시': 'BS000',
      '부산광역시': 'BS000',
      '대구': 'DG000',
      '대구시': 'DG000',
      '대구광역시': 'DG000',
      '인천': 'IC000',
      '인천시': 'IC000',
      '인천광역시': 'IC000',
      '광주': 'GJ000',
      '광주시': 'GJ000',
      '광주광역시': 'GJ000',
      '대전': 'DJ000',
      '대전시': 'DJ000',
      '대전광역시': 'DJ000',
      '울산': 'US000',
      '울산시': 'US000',
      '울산광역시': 'US000',
      '세종': 'SJ000',
      '세종시': 'SJ000',
      '세종특별자치시': 'SJ000',
      '경기': 'GG000',
      '경기도': 'GG000',
      '충북': 'CB000',
      '충청북도': 'CB000',
      '충남': 'CN000',
      '충청남도': 'CN000',
      '강원': 'GW000',
      '강원도': 'GW000',
      '강원특별자치도': 'GW000',
      '제주': 'JJ000',
      '제주도': 'JJ000',
      '제주특별자치도': 'JJ000'
    };
    
    // 지역명에서 정확한 매칭 찾기
    const normalizedRegion = regionName.trim().replace(/\s+/g, '');
    
    // 직접 매칭 시도
    for (const [key, value] of Object.entries(regionMapping)) {
      if (normalizedRegion.includes(key) || key.includes(normalizedRegion)) {
        return value;
      }
    }
    
    // 부분 매칭 시도 (예: "서울 강남구" -> "서울")
    for (const [key, value] of Object.entries(regionMapping)) {
      if (normalizedRegion.startsWith(key) || normalizedRegion.includes(key)) {
        return value;
      }
    }
    
    console.log('지역 코드 매핑 실패:', regionName);
    return '';
  }
  
  // 날짜 문자열을 현재 날짜 기준으로 보정하는 헬퍼 함수
  function normalizeProjectDates() {
    const today = new Date();
    const todayStr = getTodayString();

    // 시작일: '즉시' 또는 공백(빈 문자열)인 경우 오늘 날짜로 설정
    if (!projectData.startDate || /즉시/.test(projectData.startDate)) {
      projectData.startDate = todayStr;
    }

    // 종료일: 공백(빈 문자열)인 경우 시작일보다 5일 뒤로 설정
    if (!projectData.endDate) {
      const startDate = new Date(projectData.startDate);
      // 유효한 시작일인지 확인
      if (!isNaN(startDate.getTime())) {
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 5);
        projectData.endDate = endDate.toISOString().split('T')[0];
      } else {
        // 시작일이 유효하지 않으면 오늘부터 5일 후로 설정
        console.warn('유효하지 않은 시작일, 오늘부터 5일 후로 종료일 설정');
        const endDate = new Date(today);
        endDate.setDate(today.getDate() + 5);
        projectData.endDate = endDate.toISOString().split('T')[0];
      }
    }

    // -----------------------------
    // (1)  연도 보정 헬퍼
    // -----------------------------
    const adjustYear = (dateStr, referenceDate = today) => {
      if (!dateStr) return null;
      const iso = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!iso) return dateStr; // ISO 형식이 아니면 그대로 반환

      const [ , year, month, day ] = iso;
      const yyyy = parseInt(year, 10);
      const mm = parseInt(month, 10) - 1; // JS Date 월은 0-based
      const dd = parseInt(day, 10);
      let adjustedYear = yyyy;

      // 현재 연도보다 과거라면 보정
      if (yyyy < referenceDate.getFullYear()) {
        adjustedYear = referenceDate.getFullYear();

        // 여전히 과거라면 다음 해로 설정 (예: 오늘이 4월인데 3월 15일 등)
        const tentative = new Date(adjustedYear, mm, dd);
        if (tentative < referenceDate) {
          adjustedYear += 1;
        }
      }

      // 보정 필요 없으면 원본 유지
      if (adjustedYear === yyyy) return dateStr;
      return `${adjustedYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    };

    // 종료일 문자열이 존재할 때만 처리
    if (projectData.endDate) {
      let endStr = projectData.endDate.toString().trim();

      // 예) "11월말", "11월 말" 과 같은 패턴 매칭
      const monthEndMatch = endStr.match(/^(\d{1,2})\s*월\s*말?$/);
      if (monthEndMatch) {
        const month = parseInt(monthEndMatch[1], 10); // 1~12
        let year = today.getFullYear();

        // 이미 지난 달이라면 다음 해로 설정
        if (month < (today.getMonth() + 1)) {
          year += 1;
        }

        // 해당 월의 마지막 날짜 구하기
        const lastDay = new Date(year, month, 0).getDate();
        projectData.endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      } else {
        // 예) "17개월", "17 개월 후" 와 같은 패턴 처리
        const monthsDurationMatch = endStr.match(/^(\d+)\s*개월(?:\s*후)?$/);
        if (monthsDurationMatch) {
          const monthsToAdd = parseInt(monthsDurationMatch[1], 10);

          // 시작일이 없으면 오늘 날짜를 기준으로 함
          let start = today;
          if (projectData.startDate) {
            const startDate = new Date(projectData.startDate);
            // 유효한 날짜인지 확인
            if (!isNaN(startDate.getTime())) {
              start = startDate;
            }
          }

          // 날짜 계산: start 기준 monthsToAdd 만큼 더한 같은 일자
          const tentative = new Date(start.getFullYear(), start.getMonth() + monthsToAdd, start.getDate());

          // 유효한 날짜인지 확인 후 저장
          if (!isNaN(tentative.getTime())) {
            projectData.endDate = tentative.toISOString().split('T')[0];
          } else {
            console.warn('유효하지 않은 종료일 계산 결과, 기본값 사용');
            const fallbackEnd = new Date(start);
            fallbackEnd.setDate(start.getDate() + 30); // 30일 후로 설정
            projectData.endDate = fallbackEnd.toISOString().split('T')[0];
          }
        } else {
          // ISO 형식(YYYY-MM-DD)인 경우 보정
          endStr = adjustYear(endStr);
          projectData.endDate = endStr;
        }
      }
    }

    // -----------------------------
    // (2)  시작일이 과거 연도인 경우 보정
    // -----------------------------
    if (projectData.startDate) {
      projectData.startDate = adjustYear(projectData.startDate);
    }

    // (3)  종료일이 시작일보다 과거가 되는 경우 보정
    if (projectData.startDate && projectData.endDate) {
      const startDateObj = new Date(projectData.startDate);
      const endDateObj = new Date(projectData.endDate);
      
      // 날짜 유효성 검사
      if (!isNaN(startDateObj.getTime()) && !isNaN(endDateObj.getTime())) {
        if (endDateObj < startDateObj) {
          // 시작일과 동일 연도로 맞춤 (대부분의 케이스에서 유효)
          projectData.endDate = `${startDateObj.getFullYear()}-${String(endDateObj.getMonth() + 1).padStart(2, '0')}-${String(endDateObj.getDate()).padStart(2, '0')}`;
        }
      } else {
        console.warn('유효하지 않은 날짜로 인한 비교 실패, 날짜 보정 건너뜀');
      }
    }
  }
  
  // Claude Bridge를 통해 공고 텍스트를 분석하는 함수
  async function analyzeProjectDescription() {
    if (!originalText.trim()) {
      errorMessage = "프로젝트 공고 내용을 입력해주세요.";
      return;
    }

    try {
      isProcessing = true;
      errorMessage = "";

      const token = localStorage.getItem('token');
      if (!token) {
        errorMessage = "로그인이 필요합니다.";
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/claude/analyze-job`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ text: originalText })
      });

      const data = await response.json();

      if (response.ok) {
        processedData = data;

        // projectData에 결과 복사
        Object.keys(projectData).forEach(key => {
          if (processedData[key] !== undefined) {
            projectData[key] = processedData[key];
          }
        });

        // AI 분석 후 날짜 기본값 설정
        setDefaultDates();

        // 날짜 문자열 보정
        normalizeProjectDates();

        // 지역 코드 매핑 처리
        if (processedData.regionName) {
          const regionCode = mapRegionNameToCode(processedData.regionName);
          if (regionCode) {
            projectData.code_value = regionCode;
            setTimeout(() => { prefillRegionFromCode(regionCode); }, 100);
          }
        } else if (processedData.location) {
          const regionCode = mapRegionNameToCode(processedData.location);
          if (regionCode) {
            projectData.code_value = regionCode;
            setTimeout(() => { prefillRegionFromCode(regionCode); }, 100);
          }
        }
      } else if (response.status === 401 || response.status === 403) {
        // 인증 미들웨어는 error 가 아닌 message 로 응답하므로, 토큰 만료가
        // "AI 분석 서비스 오류"로 오인되던 문제 방지
        errorMessage = "로그인이 만료되었습니다. 로그아웃 후 다시 로그인해주세요.";
      } else {
        errorMessage = data.error || data.message || "AI 분석 서비스 오류가 발생했습니다. 다시 시도해주세요.";
      }
    } catch (error) {
      console.error("Claude 분석 오류:", error);
      errorMessage = `오류: ${error.message}`;
    } finally {
      isProcessing = false;
    }
  }
  
  // 직접 등록 모드로 전환하는 함수
  function goToManualRegistration() {
    // 프로젝트 데이터 초기화 (시작일자는 오늘, 종료일자는 5일 뒤 기본값 설정)
    projectData = {
      title: "",
      company: "",
      skills: [""],
      requirements: [""],
      preferredSkills: [""],
      level: "",
      headcount: "",
      location: "",
      code_value: "",
      salary: "",
      startDate: "",
      endDate: "",
      deadline: "", // 공고 마감일 추가
      description: "",
      contact: "",
      category: "",
      status: "active", // 기본값: 모집중
      admin_notes: "", // 관리자 비고 추가
      admin_remark: "" // 관리자 비고 2 추가
    };
    
    // 날짜 기본값 설정
    setDefaultDates();
    
    // 지역 선택 상태 초기화
    selectedCity = "";
    regionError = "";
    projectData.code_value = "";
    
    // 수동 등록 모드로 전환
    isManualRegistration = true;
    processedData = true; // 폼 표시를 위해 processedData를 true로 설정
    errorMessage = "";
  }
  
  // 원래 입력 화면으로 돌아가는 함수
  function goBackToInput() {
    isManualRegistration = false;
    processedData = null;
    errorMessage = "";
  }
  
  // 프로젝트 등록 함수 (실제로는 API로 서버에 전송)
  async function registerProject() {
    try {
      isProcessing = true;
      errorMessage = "";
      
      // 필수 입력값 확인
      if (!projectData.title) {
        errorMessage = "프로젝트 제목을 입력해주세요.";
        isProcessing = false;
        return;
      }
      
      if (!projectData.category) {
        errorMessage = "카테고리를 선택해주세요.";
        isProcessing = false;
        return;
      }

      if (!projectData.status) {
        errorMessage = "공고 상태를 선택해주세요.";
        isProcessing = false;
        return;
      }

      if (!projectData.deadline) {
        errorMessage = "공고 마감일을 선택해주세요.";
        isProcessing = false;
        return;
      }
      
      if (!selectedCity) {
        errorMessage = "시/도를 선택해주세요.";
        isProcessing = false;
        return;
      }
      
      // 로컬 스토리지에서 JWT 토큰 가져오기
      const token = localStorage.getItem('token');
      if (!token) {
        errorMessage = "로그인이 필요합니다.";
        isProcessing = false;
        return;
      }
      
      // 시작일자와 종료일자 기본값 설정 (비어있는 경우)
      setDefaultDates();
      
      // 날짜 문자열 최종 보정
      normalizeProjectDates();
      
      // 서버 API로 프로젝트 등록 요청
      const response = await fetch(`${API_BASE_URL}/api/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(projectData)
      });
      
      if (!response.ok) {
        if (!handleApiResponse(response)) return;
      }
      
      const data = await response.json();
      
      if (response.ok) {
        isRegistered = true;
        
        // 등록 후 초기화 (페이지 이동 없이 현재 페이지에 머무름)
        setTimeout(() => {
          originalText = "";
          processedData = null;
          isRegistered = false;
          // 공고 등록 페이지에 머물러서 추가 등록 가능하도록 함
        }, 3000);
      } else {
        errorMessage = data.error || "프로젝트 등록에 실패했습니다.";
      }
    } catch (error) {
      console.error("등록 오류:", error);
      errorMessage = `등록 오류: ${error.message}`;
    } finally {
      isProcessing = false;
    }
  }
  
  // 배열 항목 추가 함수
  function addArrayItem(arrayName) {
    projectData[arrayName] = [...projectData[arrayName], ""];
  }
  
  // 배열 항목 제거 함수
  function removeArrayItem(arrayName, index) {
    projectData[arrayName] = projectData[arrayName].filter((_, i) => i !== index);
  }
  
  // 프로젝트 수정 함수 추가
  function editProject(project) {
    if (project) {
      // 프로젝트 수정을 위해 localStorage에 선택된 프로젝트 정보를 저장
      localStorage.setItem('editingProject', JSON.stringify(project));
      // 프로젝트 수정 페이지로 이동
      setPage('project-edit');
    }
  }

  // 프로젝트 상세정보 모달 열기 함수
  function showProjectDetail(project) {
    selectedProjectForDetail = project;
    showProjectDetailModal = true;
  }

  // 삭제된 프로젝트 표시 옵션 변경 시 목록 새로고침
  function handleShowDeletedToggle() {
    if (activeTab === 'manage') {
      applySearchFilter();
      updatePaginatedProjects();
      saveFilterState(); // 필터링 상태 저장
    }
  }

  // 마감 제외 옵션 변경 시 필터링 다시 적용
  function handleExcludeExpiredToggle() {
    if (activeTab === 'manage') {
      applySearchFilter();
      updatePaginatedProjects();
      saveFilterState(); // 필터링 상태 저장
    }
  }
</script>

<div class="container-xl">
  <Navigation 
    {user}
    currentPage="project-register"
  />
  
  <main class="main-content">
    <div class="card">
      
      <!-- 탭 메뉴 -->
      <div class="tabs">
        <button 
          class="tab-btn {activeTab === 'register' ? 'active' : ''}" 
          on:click={() => switchTab('register')}
        >
          공고 등록
        </button>
        <button 
          class="tab-btn {activeTab === 'manage' ? 'active' : ''}" 
          on:click={() => switchTab('manage')}
        >
          공고 관리
        </button>
      </div>
      
      <!-- 등록 탭 내용 -->
      {#if activeTab === 'register'}
        <div class="tab-content">
          {#if isRegistered}
            <div class="alert alert-success text-center">
              <div class="success-icon">✓</div>
              <h3>프로젝트가 성공적으로 등록되었습니다!</h3>
            </div>
          {:else}
            {#if !processedData}
              <div class="register-section">
                <div class="form-group">
                  <label for="project-description" class="form-label">프로젝트 공고 내용</label>
                  <textarea
                    id="project-description"
                    class="form-textarea"
                    bind:value={originalText}
                    rows="15"
                    placeholder="프로젝트 공고 원문을 입력해주세요."
                  ></textarea>
                </div>
                
                {#if errorMessage}
                  <div class="alert alert-danger">{errorMessage}</div>
                {/if}
                
                <div class="btn-group">
                  <button 
                    class="btn btn-primary" 
                    on:click={analyzeProjectDescription}
                    disabled={isProcessing || !originalText.trim()}
                  >
                    {isProcessing ? "분석 중..." : "AI로 분석하기"}
                  </button>
                  <button
                    class="btn btn-secondary"
                    on:click={goToManualRegistration}
                    disabled={isProcessing}
                  >
                    직접 등록하기
                  </button>
                </div>
              </div>
            {:else}
              <div class="result-section">
                <h3>{isManualRegistration ? "프로젝트 직접 등록" : "분석 결과"}</h3>
                <p class="text-muted mb-4">
                  {#if isManualRegistration}
                    아래 항목들을 직접 입력해주세요.
                  {:else}
                    아래 내용을 검토하고 필요시 수정한 후 등록해주세요.
                  {/if}
                </p>
                
                <div class="form-grid form-grid-2">
                  <!-- 제목 -->
                  <div class="form-group">
                    <label for="project-title" class="form-label required">프로젝트 제목</label>
                    <input type="text" id="project-title" class="form-input" bind:value={projectData.title}>
                  </div>
                  
                  <!-- 회사명 -->
                  <div class="form-group">
                    <label for="company" class="form-label required">회사명</label>
                    <input type="text" id="company" class="form-input" bind:value={projectData.company}>
                  </div>
                  
                  <!-- 개발자 등급/경력 -->
                  <div class="form-group">
                    <label for="level" class="form-label">개발자 등급/경력</label>
                    <input type="text" id="level" class="form-input" bind:value={projectData.level}>
                  </div>
                  
                  <!-- 인원 수 -->
                  <div class="form-group">
                    <label for="headcount" class="form-label">인원 수</label>
                    <input type="text" id="headcount" class="form-input" bind:value={projectData.headcount}>
                  </div>
                  
                  <!-- 시/도 선택 -->
                  <div class="form-group">
                    <label for="city-select" class="form-label required">시/도</label>
                    <select 
                      id="city-select" 
                      class="form-select" 
                      bind:value={selectedCity} 
                      on:change={handleCityChange}
                      disabled={isLoadingCities}
                    >
                      <option value="">시/도를 선택하세요</option>
                      {#each cities as city}
                        <option value={city.code_value}>{city.code_name}</option>
                      {/each}
                    </select>
                    {#if isLoadingCities}
                      <small class="form-help-text loading-status">시/도 목록 로딩 중...</small>
                    {/if}
                  </div>
                  
                  
                  <!-- 근무 위치 (주소 입력) -->
                  <div class="form-group">
                    <label for="location" class="form-label">근무 위치</label>
                    <input 
                      type="text" 
                      id="location" 
                      class="form-input" 
                      bind:value={projectData.location}
                      placeholder="예: 서울시 강남구 테헤란로 123, 삼성동 코엑스몰 근처"
                      maxlength="200"
                    >
                    <small class="form-help-text">구체적인 근무 주소나 위치를 입력해주세요. (최대 200자)</small>
                  </div>
                  
                  <!-- 급여 정보 -->
                  <div class="form-group">
                    <label for="salary" class="form-label">급여 정보</label>
                    <input type="text" id="salary" class="form-input" bind:value={projectData.salary}>
                  </div>
                  
                  <!-- 시작일 -->
                  <div class="form-group">
                    <label for="startDate" class="form-label">시작일</label>
                    <input type="date" id="startDate" class="form-input" bind:value={projectData.startDate} on:change={handleStartDateChange}>
                  </div>
                  
                  <!-- 종료일 -->
                  <div class="form-group">
                    <label for="endDate" class="form-label">종료일</label>
                    <input type="date" id="endDate" class="form-input" bind:value={projectData.endDate}>
                  </div>
                  
                  <!-- 카테고리 -->
                  <div class="form-group">
                    <label for="category" class="form-label required">카테고리</label>
                    <select id="category" class="form-select" bind:value={projectData.category}>
                      <option value="">선택하세요</option>
                      {#each categories as category}
                        <option value={category}>{category}</option>
                      {/each}
                    </select>
                  </div>

                  <!-- 상태 선택 -->
                  <div class="form-group">
                    <label for="status" class="form-label required">공고 상태</label>
                    <select id="status" class="form-select" bind:value={projectData.status}>
                      {#each statusOptions as statusOption}
                        <option value={statusOption.value}>{statusOption.label}</option>
                      {/each}
                    </select>
                    <small class="form-help-text">
                      {#each statusOptions as statusOption}
                        {#if statusOption.value === projectData.status}
                          {statusOption.description}
                        {/if}
                      {/each}
                    </small>
                  </div>

                  <!-- 공고 마감일 -->
                  <div class="form-group">
                    <label for="deadline" class="form-label required">공고 마감일</label>
                    <input 
                      type="date" 
                      id="deadline" 
                      class="form-input" 
                      bind:value={projectData.deadline}
                      min={getTodayString()}
                    >
                    <small class="form-help-text">
                      공고 지원을 마감할 날짜를 선택하세요. 기본값은 오늘로부터 5일 뒤입니다.
                    </small>
                  </div>
                </div>
                
                <!-- 기술 스택 -->
                <div class="form-group">
                  <label class="form-label" for="skills">필요 기술 스택</label>
                  <div class="array-items">
                    {#each projectData.skills as skill, index}
                      <div class="input-group mb-2">
                        <input type="text" class="form-input" bind:value={projectData.skills[index]}>
                        <button class="btn btn-sm btn-danger" on:click={() => removeArrayItem('skills', index)}>×</button>
                      </div>
                    {/each}
                    <button class="btn btn-sm btn-outline btn-primary" on:click={() => addArrayItem('skills')}>+ 항목 추가</button>
                  </div>
                </div>
                
                <!-- 자격 요건 -->
                <div class="form-group">
                  <label class="form-label" for="requirements">자격 요건</label>
                  <div class="array-items">
                    {#each projectData.requirements as req, index}
                      <div class="input-group mb-2">
                        <input type="text" class="form-input" bind:value={projectData.requirements[index]}>
                        <button class="btn btn-sm btn-danger" on:click={() => removeArrayItem('requirements', index)}>×</button>
                      </div>
                    {/each}
                    <button class="btn btn-sm btn-outline btn-primary" on:click={() => addArrayItem('requirements')}>+ 항목 추가</button>
                  </div>
                </div>
                
                <!-- 우대 사항 -->
                <div class="form-group">
                  <label class="form-label" for="preferredSkills">우대 사항</label>
                  <div class="array-items">
                    {#each projectData.preferredSkills as pref, index}
                      <div class="input-group mb-2">
                        <input type="text" class="form-input" bind:value={projectData.preferredSkills[index]}>
                        <button class="btn btn-sm btn-danger" on:click={() => removeArrayItem('preferredSkills', index)}>×</button>
                      </div>
                    {/each}
                    <button class="btn btn-sm btn-outline btn-primary" on:click={() => addArrayItem('preferredSkills')}>+ 항목 추가</button>
                  </div>
                </div>
                
                <!-- 프로젝트 설명 -->
                <div class="form-group">
                  <label for="description" class="form-label required">프로젝트 상세 설명</label>
                  <textarea id="description" class="form-textarea" bind:value={projectData.description} rows="5"></textarea>
                </div>
                
                <!-- 연락처 정보 -->
                <div class="form-group">
                  <label for="contact" class="form-label required">연락처 정보</label>
                  <textarea id="contact" class="form-textarea" bind:value={projectData.contact} rows="3"></textarea>
                </div>

                <!-- 관리자 전용 비고 -->
                <div class="form-group admin-only-field">
                  <label for="admin-notes" class="form-label">비고</label>
                  <textarea 
                    id="admin-notes" 
                    class="form-textarea" 
                    bind:value={projectData.admin_notes} 
                    rows="3"
                    placeholder="비고사항을 입력하세요."
                  ></textarea>
                  
                  
                  <label for="admin-remark" class="form-label">비고 2</label>
                  <textarea 
                    id="admin-remark" 
                    class="form-textarea" 
                    bind:value={projectData.admin_remark} 
                    rows="3"
                    placeholder="비고사항을 입력하세요."
                  ></textarea>
                  <small class="form-help-text">
                    이 내용은 관리자만 볼 수 있으며, 일반 사용자에게는 표시되지 않습니다.
                  </small>
                </div>
                
                {#if errorMessage}
                  <div class="alert alert-danger">{errorMessage}</div>
                {/if}
                
                <div class="btn-group">
                  <button class="btn btn-secondary" on:click={goBackToInput}>
                    {isManualRegistration ? "이전으로" : "다시 입력하기"}
                  </button>
                  <button 
                    class="btn btn-primary" 
                    on:click={registerProject}
                    disabled={isProcessing || !projectData.title || !projectData.category || !projectData.status || !projectData.deadline || !selectedCity}
                  >
                    {isProcessing ? "등록 중..." : "프로젝트 등록하기"}
                  </button>
                </div>
              </div>
            {/if}
          {/if}
        </div>
      {:else}
        <!-- 관리 탭 내용 -->
        <div class="tab-content">
          <div class="manage-section">
              <!-- 제목 및 검색 영역 -->
              <div class="d-flex justify-between align-center mb-4 title-search-row">
                <div class="d-flex align-center gap-3">
                  <h3>등록된 공고 목록</h3>
                  <div class="checkbox-wrapper">
                    <input 
                      type="checkbox" 
                      id="showDeleted"
                      bind:checked={showDeletedProjects}
                      on:change={handleShowDeletedToggle}
                    >
                    <label for="showDeleted">삭제된 항목 포함</label>
                  </div>
                  <div class="checkbox-wrapper">
                    <input 
                      type="checkbox" 
                      id="excludeExpired"
                      bind:checked={excludeExpiredProjects}
                      on:change={handleExcludeExpiredToggle}
                    >
                    <label for="excludeExpired">마감 제외</label>
                  </div>
                </div>
                <div class="search-container">
                  <div class="search-input-group">
                    <input 
                      type="text" 
                      class="search-input"
                      placeholder="제목, 회사명, 카테고리, 마감일로 검색..."
                      bind:value={searchQuery}
                      on:input={handleSearchChange}
                    >
                    {#if searchQuery}
                      <button class="search-clear-btn" on:click={clearSearch} title="검색 초기화">
                        ✕
                      </button>
                    {/if}
                    <div class="search-icon">🔍</div>
                  </div>
                </div>
              </div>
              
              <!-- 검색 결과 정보 -->
              {#if searchQuery}
                <div class="search-result-info mb-3">
                  <span class="text-muted">'{searchQuery}' 검색 결과: {filteredProjects.length}개</span>
                </div>
              {/if}
              
              <!-- 액션 버튼 영역 -->
              <div class="d-flex justify-end align-center mb-4 actions-row">
                <div class="btn-group action-buttons-container">
                  {#if hasSelectedProjects}
                    <!-- 마감일 변경 버튼 -->
                    <button 
                      class="btn btn-info" 
                      type="button" 
                      on:click={openDeadlineModal}
                      disabled={isUpdatingDeadline || isUpdatingStatus}
                    >
                      📅 {isUpdatingDeadline ? "변경 중..." : `마감일 변경 (${selectedProjects.length}개)`}
                    </button>
                    
                    <!-- 상태 변경 드롭다운 -->
                    <div class="dropdown">
                      <button 
                        class="btn btn-primary dropdown-toggle" 
                        type="button" 
                        on:click={() => showStatusDropdown = !showStatusDropdown}
                        disabled={isUpdatingStatus}
                      >
                        {isUpdatingStatus ? "처리 중..." : `선택한 ${selectedProjects.length}개 상태 변경`}
                        <span class="dropdown-arrow">▼</span>
                      </button>
                      {#if showStatusDropdown}
                        <div class="dropdown-menu show">
                          <button 
                            class="dropdown-item"
                            on:click={() => handleStatusChange('active')}
                            disabled={isUpdatingStatus}
                          >
                            <span class="status-icon success">●</span>
                            모집중으로 변경
                          </button>
                          <button 
                            class="dropdown-item"
                            on:click={() => handleStatusChange('open')}
                            disabled={isUpdatingStatus}
                          >
                            <span class="status-icon warning">●</span>
                            채용 시 마감으로 변경
                          </button>
                          <button 
                            class="dropdown-item"
                            on:click={() => handleStatusChange('lapse')}
                            disabled={isUpdatingStatus}
                          >
                            <span class="status-icon secondary">●</span>
                            마감으로 변경
                          </button>
                          <button 
                            class="dropdown-item"
                            on:click={() => handleStatusChange('closed')}
                            disabled={isUpdatingStatus}
                          >
                            <span class="status-icon info">●</span>
                            상시 채용으로 변경
                          </button>
                          <div class="dropdown-divider"></div>
                          <button 
                            class="dropdown-item text-danger"
                            on:click={() => handleStatusChange('delete')}
                            disabled={isDeletingProjects || isUpdatingStatus}
                          >
                            <span class="status-icon danger">🗑</span>
                            {isDeletingProjects ? "삭제 중..." : "삭제"}
                          </button>
                        </div>
                      {/if}
                    </div>
                  {/if}
                  <button class="btn btn-secondary" on:click={() => loadProjects()} disabled={isLoadingProjects || isUpdatingStatus}>
                    🔄 {isLoadingProjects ? "로딩 중..." : "새로고침"}
                  </button>
                  <button class="btn btn-outline-secondary" on:click={resetAllFilters} disabled={isLoadingProjects || isUpdatingStatus}>
                    🔄 초기화
                  </button>
                  
                  <!-- 페이지당 표시수 드롭다운 -->
                  <select 
                    bind:value={paginationPageSize}
                    class="page-size-select"
                  >
                    <option value={10}>10개</option>
                    <option value={20}>20개</option>
                    <option value={50}>50개</option>
                    <option value={100}>100개</option>
                  </select>
                </div>
              </div>
            
            {#if projectError}
              <div class="alert alert-danger">{projectError}</div>
            {/if}
            
            {#if isLoadingProjects}
              <div class="text-center py-5">
                <div class="loading-spinner"></div>
                <p class="text-muted mt-3">공고 목록을 불러오는 중...</p>
              </div>
            {:else if filteredProjects.length === 0}
              <div class="text-center py-5">
                {#if searchQuery}
                  <EmptyState 
                    title="검색 결과가 없습니다" 
                    description="'{searchQuery}'에 해당하는 공고를 찾을 수 없습니다." 
                    icon="🔍"
                  />
                {:else}
                  <EmptyState 
                    title="등록된 공고가 없습니다" 
                    description="새로운 프로젝트 공고를 등록해보세요." 
                    icon="📋"
                  />
                {/if}
              </div>
            {:else}
              <!-- 그리드 정보 -->
              <div class="grid-header-controls">
                <div class="grid-info">
                  <span class="text-muted">총 {filteredProjects.length}개 프로젝트</span>
                    </div>
                  </div>
                  
              <!-- AG-Grid -->
              <div class="ag-theme-alpine grid-container">
                <AgGridSvelte
                  bind:api={gridApi}
                  rowData={filteredProjects}
                  columnDefs={projectColumnDefs}
                  defaultColDef={gridOptions.defaultColDef}
                  rowSelection="multiple"
                  suppressRowClickSelection={true}
                  pagination={true}
                  paginationPageSize={paginationPageSize}
                  paginationPageSizeSelector={false}
                  paginationAutoPageSize={false}
                  animateRows={true}
                  domLayout={gridOptions.domLayout}
                  components={gridOptions.components}
                  suppressRowDeselection={false}
                  getRowId={gridOptions.getRowId}
                  deltaSort={gridOptions.deltaSort}
                  suppressClearOnFillReduction={gridOptions.suppressClearOnFillReduction}
                  localeText={{
                    pageSizeSelectorLabel: '페이지당 항목 수:',
                    of: ' / ',
                    to: ' - ',
                    page: '페이지',
                    firstPage: '첫 페이지',
                    previousPage: '이전 페이지',
                    nextPage: '다음 페이지',
                    lastPage: '마지막 페이지'
                  }}
                  onGridReady={onGridReady}
                  onSelectionChanged={onSelectionChanged}
                  onRowClicked={onRowClicked}
                  onCellClicked={onCellClicked}
                  onFilterChanged={onFilterChanged}
                />
                </div>
            {/if}
          </div>
        </div>
      {/if}
    </div>
  </main>
</div>

<!-- 프로젝트 상세정보 모달 -->
<ProjectDetailModal 
  bind:isOpen={showProjectDetailModal}
  project={selectedProjectForDetail}
  isAdmin={true}
/>

<!-- 마감일 변경 모달 -->
{#if showDeadlineModal}
  <div class="modal-overlay" on:click={closeDeadlineModal}>
    <div class="deadline-modal" on:click|stopPropagation>
      <div class="modal-header">
        <h3>공고 마감일 변경</h3>
        <button class="modal-close-btn" on:click={closeDeadlineModal}>×</button>
      </div>
      
      <div class="modal-body">
        <p class="modal-description">
          선택한 <strong>{selectedProjects.length}개</strong>의 공고 마감일을 변경합니다.
        </p>
        
        <div class="form-group">
          <label for="new-deadline" class="form-label required">새로운 마감일</label>
          <input 
            type="date" 
            id="new-deadline" 
            class="form-input" 
            bind:value={newDeadline}
            min={getTodayString()}
          />
          <small class="form-help-text">
            마감일은 오늘 이후 날짜로 설정해주세요.
          </small>
        </div>
        
        {#if projectError}
          <div class="alert alert-danger mb-3">{projectError}</div>
        {/if}
      </div>
      
      <div class="modal-footer">
        <button 
          class="btn btn-secondary" 
          on:click={closeDeadlineModal}
          disabled={isUpdatingDeadline}
        >
          취소
        </button>
        <button 
          class="btn btn-primary" 
          on:click={updateProjectsDeadline}
          disabled={isUpdatingDeadline || !newDeadline}
        >
          {isUpdatingDeadline ? "변경 중..." : "마감일 변경"}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  
  /* 누락된 CSS 클래스들 */
  .tabs {
    display: flex;
    border-bottom: 2px solid var(--color-border, #e9ecef);
    margin-bottom: var(--spacing-lg, 1.5rem);
  }

  .tab-btn {
    background: none;
    border: none;
    padding: var(--spacing-md, 1rem) var(--spacing-lg, 1.5rem);
    cursor: pointer;
    font-size: 1rem;
    font-weight: 500;
    color: var(--color-text-secondary, #6c757d);
    border-bottom: 2px solid transparent;
    transition: all 0.3s ease;
  }

  .tab-btn:hover {
    color: var(--color-primary, #007bff);
    background-color: var(--color-light, #f8f9fa);
  }

  .tab-btn.active {
    color: var(--color-primary, #007bff);
    border-bottom-color: var(--color-primary, #007bff);
    background-color: transparent;
  }

  .tab-content {
    padding: var(--spacing-lg, 1.5rem);
  }

  .main-content {
    padding: var(--spacing-lg, 1.5rem) 0;
    min-height: calc(100vh - 80px);
    max-width: 2500px; /* 1800 → 2000 */
    margin: 0 auto;
  }

  :global(.navbar-actions) {
    display: none;
  }

  /* 등록 섹션 패딩 추가 */
  .register-section {
    padding: var(--spacing-lg, 1.5rem);
    background-color: var(--color-white, #fff);
    border-radius: 8px;
    border: 0px solid var(--color-border, #e9ecef);
    margin-bottom: var(--spacing-md, 1rem);
  }

  .result-section {
    padding: var(--spacing-lg, 1.5rem);
    background-color: var(--color-white, #fff);
    border-radius: 8px;
    border: 0px solid var(--color-border, #e9ecef);
    margin-bottom: var(--spacing-md, 1rem);
  }

  @media (max-width: 768px) {
    .tab-content {
      padding: var(--spacing-md, 1rem);
    }

    .register-section,
    .result-section {
      padding: var(--spacing-md, 1rem);
    }

    :global(.navbar-actions) {
      display: none;
    }
  }

  .loading-spinner {
    border: 4px solid var(--color-border, #e9ecef);
    border-top: 4px solid var(--color-primary, #007bff);
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin: 0 auto;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .success-icon {
    font-size: 3rem;
    color: var(--color-success, #28a745);
    margin-bottom: var(--spacing-md, 1rem);
  }

  .form-grid-2 {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: var(--spacing-md, 1rem);
  }

  .array-items {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm, 0.5rem);
  }

  .input-group {
    display: flex;
    gap: var(--spacing-sm, 0.5rem);
    align-items: center;
  }

  .input-group .form-input {
    flex: 1;
  }

  .alert-success {
    background-color: #d4edda;
    border-color: #c3e6cb;
    color: #155724;
  }

  .text-muted {
    color: var(--color-text-secondary, #6c757d) !important;
  }

  .py-5 {
    padding-top: var(--spacing-xl, 2rem) !important;
    padding-bottom: var(--spacing-xl, 2rem) !important;
  }

  .mt-3 {
    margin-top: var(--spacing-md, 1rem) !important;
  }

  .mb-2 {
    margin-bottom: var(--spacing-sm, 0.5rem) !important;
  }

  .mb-3 {
    margin-bottom: var(--spacing-md, 1rem) !important;
  }

  .mb-4 {
    margin-bottom: var(--spacing-lg, 1.5rem) !important;
  }

  .required::after {
    content: " *";
    color: var(--color-danger, #dc3545);
  }


  /* 버튼 그룹 간격 조정 */
  .btn-group {
    display: flex;
    gap: var(--spacing-sm, 0.5rem);
    flex-wrap: nowrap;
  }

  /* 액션 버튼 컨테이너 */
  .action-buttons-container {
    display: flex;
    gap: var(--spacing-sm, 0.5rem);
    flex-wrap: nowrap;
    align-items: center;
  }

  .action-buttons-container .btn {
    white-space: nowrap;
    font-size: 0.875rem;
    padding: 0.5rem 0.75rem;
  }

  /* 페이지당 표시수 드롭다운 스타일 */
  .page-size-select {
    background-color: white;
    border: 2px solid var(--color-primary, #007bff);
    border-radius: 6px;
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--color-text, #333);
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 80px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    margin-left: 0.5rem;
  }

  .page-size-select:hover {
    border-color: var(--color-primary, #007bff);
    box-shadow: 0 2px 6px rgba(0, 123, 255, 0.3);
    transform: translateY(-1px);
  }

  .page-size-select:focus {
    outline: none;
    border-color: var(--color-primary, #007bff);
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }


  /* 드롭다운 스타일 */
  .dropdown {
    position: relative;
    display: inline-block;
  }

  .dropdown-toggle {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .dropdown-arrow {
    font-size: 0.75rem;
    transition: transform 0.2s ease;
  }


  .dropdown-menu {
    position: absolute;
    top: 100%;
    right: 0;
    background: white;
    border: 1px solid var(--color-border, #e9ecef);
    border-radius: 6px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    min-width: 220px;
    z-index: 1000;
    display: none;
    padding: 0.5rem 0;
    margin-top: 2px;
  }

  .dropdown-menu.show {
    display: block;
  }

  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.5rem 1rem;
    border: none;
    background: none;
    text-align: left;
    font-size: 0.875rem;
    color: var(--color-text, #333);
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  .dropdown-item:hover {
    background-color: var(--color-gray-100, #f8f9fa);
  }

  .dropdown-item:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .dropdown-item.text-danger {
    color: var(--color-danger, #dc3545);
  }

  .dropdown-divider {
    height: 1px;
    background-color: var(--color-border, #e9ecef);
    margin: 0.5rem 0;
  }

  .status-icon {
    font-size: 0.875rem;
    width: 16px;
    text-align: center;
  }

  .status-icon.success {
    color: #22c55e;
  }

  .status-icon.warning {
    color: #f59e0b;
  }

  .status-icon.secondary {
    color: #6b7280;
  }

  .status-icon.info {
    color: #3b82f6;
  }

  .status-icon.danger {
    color: #ef4444;
  }
  
  .status-icon.calendar {
    color: #8b5cf6;
  }

  /* 초기화 버튼 스타일 */
  .btn-outline-secondary {
    background-color: transparent;
    border: 2px solid #6c757d;
    color: #6c757d;
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
  }

  .btn-outline-secondary:hover {
    background-color: #6c757d;
    color: white;
    border-color: #6c757d;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(108, 117, 125, 0.2);
  }

  .btn-outline-secondary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  .btn-outline-secondary:disabled:hover {
    background-color: transparent;
    color: #6c757d;
    border-color: #6c757d;
  }

  @media (max-width: 768px) {
    .btn-group {
      justify-content: flex-end;
    }

    .action-buttons-container {
      flex-wrap: nowrap;
      overflow-x: auto;
      gap: 0.25rem;
    }

    .action-buttons-container .btn {
      font-size: 0.75rem;
      padding: 0.375rem 0.5rem;
      min-width: auto;
      flex-shrink: 0;
    }

    .btn-outline-secondary {
      font-size: 0.75rem;
      padding: 0.375rem 0.5rem;
      border-width: 1px;
    }

    /* 모바일에서 페이지당 표시수 드롭다운 */
    .page-size-select {
      min-width: 70px;
      padding: 0.375rem 0.5rem;
      font-size: 0.75rem;
      margin-left: 0.25rem;
    }

  }

  /* 추가 유틸리티 클래스 */
  .gap-3 {
    gap: var(--spacing-md, 1rem);
  }

  .align-center {
    align-items: center;
  }

  .d-flex {
    display: flex;
  }

  .justify-between {
    justify-content: space-between;
  }

  /* 삭제된 행 스타일 */
  :global(.deleted-row) {
    opacity: 0.6;
    background-color: #f8f9fa !important;
    color: #6c757d;
  }

  :global(.deleted-row:hover) {
    background-color: #e9ecef !important;
  }

  /* 삭제됨 상태 배지 스타일 */
  :global(.status-badge.deleted) {
    background-color: #dc3545;
    color: white;
  }

  /* 제목 및 검색 영역 스타일 */
  .title-search-row {
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 1rem 1.5rem;
    border: 1px solid var(--color-border, #e9ecef);
    gap: 1rem;
    flex-wrap: wrap;
    align-items: center;
  }

  /* 액션 버튼 영역 스타일 */
  .actions-row {
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .search-container {
    display: flex;
    align-items: center;
    max-width: 350px;
    min-width: 280px;
  }

  .search-input-group {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    max-width: 350px;
    height: 42px; /* 버튼과 동일한 높이로 고정 */
  }

  .search-input {
    width: 100%;
    padding: 0.5rem 3rem 0.5rem 1rem;
    border: 1px solid var(--color-border, #dee2e6);
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 400;
    background-color: var(--color-white, #fff);
    color: var(--color-text, #495057);
    transition: all 0.15s ease-in-out;
    box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075);
    height: 100%; /* 부모 컨테이너 높이에 맞춤 */
    line-height: 1.5;
    vertical-align: middle; /* 세로 중간 정렬 */
    box-sizing: border-box; /* 패딩과 테두리 포함한 크기 계산 */
  }

  .search-input:focus {
    border-color: var(--color-primary, #80bdff);
    outline: 0;
    box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }

  .search-input::placeholder {
    color: var(--color-text-secondary, #6c757d);
  }

  .search-icon {
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%); /* 완전한 중앙 정렬 */
    color: var(--color-text-secondary, #6c757d);
    font-size: 0.9rem;
    pointer-events: none;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .search-clear-btn {
    position: absolute;
    right: 2.5rem;
    top: 50%;
    transform: translateY(-50%); /* 완전한 중앙 정렬 */
    background: none;
    border: none;
    color: var(--color-text-secondary, #6c757d);
    cursor: pointer;
    font-size: 0.9rem;
    padding: 0.25rem;
    border-radius: 4px;
    transition: all 0.2s ease;
    z-index: 2;
    height: 24px;
    width: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .search-clear-btn:hover {
    background-color: var(--color-gray-200, #f1f3f5);
    color: var(--color-danger, #dc3545);
  }

  .search-result-info {
    font-size: 0.875rem;
    color: var(--color-text-secondary, #6c757d);
    padding: 0.5rem 1rem;
    background-color: #e3f2fd;
    border-radius: 6px;
    border-left: 3px solid var(--color-primary, #007bff);
  }

  .search-result-info .text-muted {
    font-style: italic;
    color: var(--color-primary, #007bff) !important;
    font-weight: 500;
  }

  /* 모바일 반응형 */
  @media (max-width: 768px) {
    .title-search-row {
      flex-direction: column;
      align-items: stretch;
      gap: 1rem;
      padding: 1rem;
    }

    .title-search-row .d-flex {
      flex-direction: column;
      align-items: stretch;
      gap: 1rem;
    }

    .search-container {
      max-width: 100%;
      order: -1; /* 검색창을 맨 위로 이동 */
    }

    .search-input-group {
      max-width: 100%;
    }

    .actions-row {
      justify-content: center;
    }

    .btn-group {
      justify-content: center;
      flex-wrap: wrap;
    }

    .btn-group button {
      font-size: 0.8rem;
      padding: 0.5rem 0.75rem;
    }

    h3 {
      text-align: center;
      margin: 0;
    }

    .checkbox-wrapper {
      justify-content: center;
      margin-left: 0;
    }

    /* 모바일에서 관리 섹션 패딩 조정 */
    .manage-section {
      padding: var(--spacing-md, 0rem);
      margin-bottom: var(--spacing-sm, 0.5rem);
    }

    /* 제목 및 검색 영역 모바일 조정 */
    .title-search-row {
      padding: 0.75rem;
    }

    /* 액션 버튼 영역 모바일 조정 */
    .actions-row {
      padding: 0 0.5rem;
    }
  }

  /* 체크박스 스타일 개선 */
  .checkbox-wrapper {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin-left: var(--spacing-sm, 0.5rem);
  }

  .checkbox-wrapper input[type="checkbox"] {
    width: 1rem;
    height: 1rem;
    cursor: pointer;
    margin: 0;
  }

  .checkbox-wrapper label {
    font-size: 0.875rem;
    color: var(--color-text-secondary, #6c757d);
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
    margin: 0;
  }

  /* 마감 제외 체크박스 특별 스타일 */
  .checkbox-wrapper input[type="checkbox"]:checked + label[for="excludeExpired"] {
    color: var(--color-primary, #007bff);
    font-weight: 500;
  }

  .checkbox-wrapper input[type="checkbox"]#excludeExpired:checked {
    accent-color: var(--color-primary, #007bff);
  }

  /* 공고 관리 섹션 패딩 추가 */
  .manage-section {
    padding: var(--spacing-lg, rem);
    background-color: var(--color-white, #fff);
    border-radius: 8px;
    border: 0px solid var(--color-border, #e9ecef);
    margin-bottom: var(--spacing-md, 1rem);
  }

  /* 테이블 제목 호버 색상 수정 */
  :global(.data-table th:hover) {
    color: var(--color-text, #333) !important;
    background-color: var(--color-gray-100, #f8f9fa) !important;
  }

  :global(.data-table th a:hover) {
    color: var(--color-text, #333) !important;
  }

  /* 상단 고정 체크박스 스타일링 */
  .pin-checkbox-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }

  .pin-checkbox {
    width: 16px;
    height: 16px;
    cursor: pointer;
    transform: scale(1.2);
  }

  .pin-checkbox:checked {
    accent-color: var(--color-primary, #007bff);
  }

  :global(.data-table thead th:hover) {
    color: var(--color-text, #333) !important;
  }

  /* 테이블 헤더 가운데 정렬 */
  :global(thead th) {
    text-align: center !important;
    vertical-align: middle !important;
  }

  :global(table th) {
    text-align: center !important;
  }

  :global(.table-header-content) {
    justify-content: center !important;
    text-align: center !important;
    display: flex !important;
    align-items: center !important;
  }

  :global(.sort-indicator) {
    margin-left: 0.25rem !important;
  }

  :global(.table-select-header) {
    text-align: center !important;
  }

  /* 공고 마감일 컬럼 스타일 */
  :global(.data-table .table-cell-deadline) {
    font-weight: 600;
    color: var(--color-text, #333);
  }

  /* 마감 임박 스타일 (3일 이내) */
  :global(.data-table .table-cell-deadline.deadline-soon) {
    color: var(--color-warning, #f39c12) !important;
    background-color: #fef9e7 !important;
  }

  /* 마감 당일 스타일 */
  :global(.data-table .table-cell-deadline.deadline-today) {
    color: var(--color-danger, #dc3545) !important;
    background-color: #fdebee !important;
    animation: pulse 2s infinite;
  }

  /* 마감 지남 스타일 */
  :global(.data-table .table-cell-deadline.deadline-expired) {
    color: var(--color-gray-500, #6c757d) !important;
    background-color: var(--color-gray-100, #f8f9fa) !important;
    text-decoration: line-through;
  }

  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.7; }
    100% { opacity: 1; }
  }

  /* 작업 컬럼 가운데 정렬 */
  :global(.data-table td:last-child),
  :global(.data-table th:last-child) {
    text-align: center !important;
  }

  :global(.data-table .actions-cell) {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: var(--spacing-sm, 0.5rem);
  }

  /* 테이블 정렬 화살표 중복 제거 */
  :global(.data-table .sort-indicator) {
    display: none !important;
  }

  :global(.data-table th.sorted .sort-indicator) {
    display: none !important;
  }

  :global(.data-table th:not(.sortable) .sort-indicator) {
    display: none !important;
  }


  /* form-help-text 스타일 추가 */
  .form-help-text {
    display: block;
    margin-top: 0.25rem;
    font-size: 0.875rem;
    color: var(--color-text-secondary, #6c757d);
    font-style: italic;
  }

  /* 클릭 가능한 제목 스타일 */
  :global(.data-table .clickable-cell) {
    cursor: pointer !important;
    color: var(--color-primary, #007bff) !important;
    text-decoration: underline !important;
    font-weight: 500 !important;
  }

  :global(.data-table .clickable-cell:hover) {
    color: var(--color-primary-dark, #0056b3) !important;
    text-decoration: underline !important;
  }

  /* 관리자 전용 필드 스타일 */
  .admin-only-field {
    border: 2px solid var(--color-primary, #007bff);
    border-radius: 8px;
    padding: var(--spacing-md, 1rem);
    background-color: #f8f9ff;
    position: relative;
    margin-top: var(--spacing-lg, 1.5rem);
  }

  .admin-only-field::before {
    content: "👑 관리자 전용";
    position: absolute;
    top: -12px;
    left: 12px;
    background-color: var(--color-primary, #007bff);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .admin-only-field label {
    color: var(--color-primary, #007bff);
    font-weight: 600;
  }

  .admin-only-field .form-help-text {
    color: var(--color-primary, #007bff);
    font-weight: 500;
  }
  
  /* 지역 선택 상태 스타일 */
  .loading-status {
    color: var(--color-primary, #007bff) !important;
    font-style: italic;
  }
  
  .error-status {
    color: var(--color-danger, #dc3545) !important;
    font-weight: 500;
  }

  /* 관리자 비고 컬럼 스타일 */
  :global(.data-table .table-cell-admin_notes) {
    background-color: #f8f9ff !important;
    border-left: 3px solid var(--color-primary, #007bff) !important;
    font-style: italic;
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.875rem;
  }

  :global(.data-table .table-cell-admin_notes:hover) {
    background-color: #e6f0ff !important;
    overflow: visible;
    white-space: normal;
    z-index: 10;
    position: relative;
    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
  }

  /* 관리자 비고 헤더 스타일 */
  :global(.data-table th:has(.table-header-content:contains("관리자 비고"))) {
    background-color: var(--color-primary, #007bff) !important;
    color: white !important;
  }

  :global(.data-table th:has(.table-header-content:contains("관리자 비고")) .table-header-content) {
    color: white !important;
  }

  /* AG-Grid 스타일 */
  .grid-container {
    width: 100%;
    margin-top: 1rem;
  }

  .grid-header-controls {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-bottom: 1rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--color-border, #e9ecef);
  }

  .grid-info {
    color: var(--color-text-secondary, #6c757d);
    font-size: 0.875rem;
  }

  /* AG-Grid 커스텀 스타일 */
  :global(.ag-theme-alpine) {
    --ag-header-height: 45px;
    --ag-row-height: 50px;
    --ag-font-size: 14px;
    --ag-font-family: inherit;
    --ag-header-background-color: #f8f9fa;
    --ag-header-foreground-color: #333;
    --ag-odd-row-background-color: #ffffff;
    --ag-even-row-background-color: #f8f9fa;
    --ag-row-hover-color: #e3f2fd;
    --ag-selected-row-background-color: #e3f2fd;
    --ag-border-color: #e9ecef;
  }

  :global(.ag-theme-alpine .ag-header-cell) {
    font-weight: 600;
    border-right: 1px solid var(--ag-border-color);
  }

  :global(.ag-theme-alpine .ag-cell) {
    border-right: 1px solid var(--ag-border-color);
    display: flex;
    align-items: center;
    padding: 0 8px;
  }

  :global(.ag-theme-alpine .ag-row) {
    border-bottom: 1px solid var(--ag-border-color);
  }

  :global(.ag-theme-alpine .ag-row:hover) {
    background-color: var(--ag-row-hover-color);
  }

  /* AG-Grid 버튼 스타일 */
  :global(.ag-theme-alpine .action-buttons) {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    justify-content: center;
    width: 100%;
  }

  :global(.ag-theme-alpine .btn-edit) {
    background-color: var(--color-primary, #007bff);
    color: white;
    border: none;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  :global(.ag-theme-alpine .btn-edit:hover) {
    background-color: #0056b3;
  }

  :global(.ag-theme-alpine .btn-delete) {
    background-color: var(--color-danger, #dc3545);
    color: white;
    border: none;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  :global(.ag-theme-alpine .btn-delete:hover) {
    background-color: #c82333;
  }

  /* AG-Grid 체크박스 스타일 */
  :global(.ag-theme-alpine .ag-selection-checkbox) {
    margin: 0 auto;
  }

  :global(.ag-theme-alpine .ag-checkbox-input-wrapper) {
    width: 18px;
    height: 18px;
  }

  /* AG-Grid 페이지네이션 스타일 */
  :global(.ag-theme-alpine .ag-paging-panel) {
    border-top: 1px solid var(--ag-border-color);
    padding: 1rem;
    background-color: #f8f9fa;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
  }

  /* 페이지네이션 왼쪽 영역 (버튼들) */
  :global(.ag-theme-alpine .ag-paging-button-wrapper) {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
  }

  /* 페이지 크기 선택기를 우측에 배치 */
  :global(.ag-theme-alpine .ag-paging-page-size) {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--color-text, #333);
    background-color: #f8f9fa;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    border: 1px solid #e9ecef;
    order: 999; /* 가장 우측에 배치 */
    margin-left: auto; /* 우측으로 밀어내기 */
  }

  :global(.ag-theme-alpine .ag-paging-page-size select) {
    background-color: white;
    border: 2px solid var(--color-primary, #007bff);
    border-radius: 6px;
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--color-text, #333);
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 80px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  :global(.ag-theme-alpine .ag-paging-page-size select:hover) {
    border-color: var(--color-primary, #007bff);
    box-shadow: 0 2px 6px rgba(0, 123, 255, 0.3);
    transform: translateY(-1px);
  }

  :global(.ag-theme-alpine .ag-paging-page-size select:focus) {
    outline: none;
    border-color: var(--color-primary, #007bff);
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }

  :global(.ag-theme-alpine .ag-paging-button) {
    background-color: white;
    border: 1px solid var(--ag-border-color);
    color: var(--color-text, #333);
    margin: 0 2px;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  :global(.ag-theme-alpine .ag-paging-button:hover) {
    background-color: var(--color-primary, #007bff);
    color: white;
    border-color: var(--color-primary, #007bff);
  }

  :global(.ag-theme-alpine .ag-paging-button.ag-disabled) {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* AG-Grid 필터 스타일 */
  :global(.ag-theme-alpine .ag-filter-wrapper) {
    background-color: white;
    border: 1px solid var(--ag-border-color);
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  /* AG-Grid 로딩 오버레이 스타일 */
  :global(.ag-theme-alpine .ag-overlay-loading-wrapper) {
    background-color: rgba(255, 255, 255, 0.9);
  }

  :global(.ag-theme-alpine .ag-overlay-loading-center) {
    background-color: white;
    border: 1px solid var(--ag-border-color);
    border-radius: 4px;
    padding: 1rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  /* 모바일 카드 스타일 */
  :global(.mobile-project-card) {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    margin-bottom: 12px;
    padding: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: all 0.2s;
    width: 100%; /* 모바일 카드 폭 고정 */
  }

  :global(.mobile-project-card:hover) {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    border-color: #d1d5db;
  }

  :global(.mobile-project-card .card-header) {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 12px;
  }

  :global(.mobile-project-card .checkbox-container) {
    flex-shrink: 0;
    padding-top: 2px;
  }

  :global(.mobile-project-card .mobile-checkbox) {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }

  :global(.mobile-project-card .project-title) {
    flex: 1;
    font-size: 16px;
    font-weight: 600;
    color: black;
    cursor: pointer;
    text-decoration: none;
    line-height: 1.4;
  }

  :global(.mobile-project-card .project-title:hover) {
    color: black;
    text-decoration: underline;
  }

  :global(.mobile-project-card .badges) {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex-shrink: 0;
  }

  :global(.mobile-project-card .project-deleted-tag) {
    background-color: #ef4444;
    color: #ffffff;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
    text-align: center;
  }

  :global(.mobile-project-card .card-body) {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  :global(.mobile-project-card .card-row) {
    display: flex;
    align-items: center;
    font-size: 14px;
    line-height: 1.4;
  }

  :global(.mobile-project-card .card-row strong) {
    color: #374151;
    font-weight: 600;
    min-width: 100px; /* 간격 확장 */
    margin-right: 12px; /* 간격 확장 */
  }

  :global(.mobile-project-card .card-row.deadline-expired) {
    color: #ef4444;
  }

  :global(.mobile-project-card .card-row.deadline-today) {
    color: #f59e0b;
    font-weight: 600;
  }

  :global(.mobile-project-card .card-row.deadline-soon) {
    color: #f59e0b;
  }

  :global(.mobile-project-card .card-actions) {
    display: flex;
    gap: 8px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #f3f4f6;
  }

  :global(.mobile-project-card .mobile-action-btn) {
    padding: 6px 12px;
    border: 1px solid #d1d5db;
    background-color: #ffffff;
    color: #374151;
    border-radius: 4px;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s;
    flex: 1;
  }

  :global(.mobile-project-card .mobile-action-btn:hover) {
    background-color: #f3f4f6;
    border-color: #9ca3af;
  }

  :global(.mobile-project-card .mobile-action-btn.btn-delete) {
    background-color: #ef4444;
    color: #ffffff;
    border-color: #ef4444;
  }

  :global(.mobile-project-card .mobile-action-btn.btn-delete:hover) {
    background-color: #dc2626;
    border-color: #dc2626;
  }

  /* 반응형 스타일 */
  @media (max-width: 768px) {
    .grid-container {
      min-height: 400px;
    }

    .grid-header-controls {
      flex-direction: column;
      gap: 0.5rem;
      align-items: stretch;
    }

    .grid-info {
      text-align: center;
    }

    /* 모바일에서 페이지네이션 패널 스타일 */
    :global(.ag-theme-alpine .ag-paging-panel) {
      flex-direction: column;
      gap: 0.75rem;
      padding: 0.75rem;
    }

    /* 모바일에서 페이지 크기 선택기를 하단에 배치 */
    :global(.ag-theme-alpine .ag-paging-page-size) {
      order: 1; /* 페이지네이션 버튼들 다음에 배치 */
      justify-content: center;
      margin-left: 0;
      margin-top: 0.5rem;
      padding: 0.75rem 1rem;
      background-color: #e3f2fd;
      border: 2px solid var(--color-primary, #007bff);
      width: 100%;
      box-sizing: border-box;
    }

    :global(.ag-theme-alpine .ag-paging-page-size select) {
      min-width: 100px;
      padding: 0.5rem 1rem;
      font-size: 1rem;
      font-weight: 600;
      border: 2px solid var(--color-primary, #007bff);
    }

    :global(.ag-theme-alpine) {
      --ag-header-height: 0px;
      --ag-row-height: auto;
      --ag-font-size: 13px;
    }

    :global(.ag-theme-alpine .ag-header) {
      display: none;
    }

    :global(.ag-theme-alpine .ag-cell) {
      padding: 0;
      border: none;
      display: block; /* 셀 내부를 block으로 설정하여 카드 전체 너비 사용 */
    }

    :global(.ag-theme-alpine .ag-row) {
      border: none;
      margin-bottom: 8px;
    }

    :global(.ag-theme-alpine .ag-row:hover) {
      background-color: transparent;
    }

    :global(.ag-theme-alpine .action-buttons) {
      flex-direction: column;
      gap: 0.25rem;
    }

    :global(.ag-theme-alpine .btn-edit),
    :global(.ag-theme-alpine .btn-delete) {
      font-size: 0.7rem;
      padding: 0.2rem 0.4rem;
    }

    :global(.mobile-project-card .card-row strong) {
      min-width: 90px; /* 간격 확장 */
      font-size: 13px;
      margin-right: 10px; /* 간격 확장 */
    }

    :global(.mobile-project-card .card-row) {
      font-size: 13px;
    }

    :global(.mobile-project-card .project-title) {
      font-size: 15px;
    }
  }

  /* 태블릿 사이즈 */
  @media (max-width: 1024px) and (min-width: 769px) {
    .grid-header-controls {
      flex-wrap: wrap;
      gap: 0.5rem;
    }
  }


  /* 페이징 관련 스타일 제거 - AG-Grid 내장 페이징 사용 */

  /* 삭제된 행 스타일 */
  :global(.deleted-row) {
    background-color: #fce8e6 !important;
    font-style: italic;
  }

  @media (max-width: 768px) {

    /* quick-filter 클래스 제거 - 사용되지 않음 */

    .grid-info {
      text-align: center;
      margin-bottom: 0.5rem;
    }
  }

  /* 마감일 변경 모달 스타일 */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
    padding: 1rem;
  }

  .deadline-modal {
    background: white;
    border-radius: 8px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
    max-width: 500px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    animation: modalSlideIn 0.3s ease-out;
  }

  @keyframes modalSlideIn {
    from {
      transform: translateY(-50px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 1.5rem 1rem 1.5rem;
    border-bottom: 1px solid var(--color-border, #e9ecef);
  }

  .modal-header h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--color-text, #333);
  }

  .modal-close-btn {
    background: none;
    border: none;
    font-size: 1.5rem;
    color: var(--color-text-secondary, #6c757d);
    cursor: pointer;
    padding: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.2s ease;
  }

  .modal-close-btn:hover {
    background-color: var(--color-gray-100, #f8f9fa);
    color: var(--color-text, #333);
  }

  .modal-body {
    padding: 1.5rem;
  }

  .modal-description {
    margin: 0 0 1.5rem 0;
    color: var(--color-text-secondary, #6c757d);
    font-size: 0.95rem;
    line-height: 1.5;
  }

  .modal-description strong {
    color: var(--color-primary, #007bff);
    font-weight: 600;
  }

  .modal-footer {
    padding: 1rem 1.5rem 1.5rem 1.5rem;
    border-top: 1px solid var(--color-border, #e9ecef);
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
  }

  .modal-footer .btn {
    min-width: 100px;
  }

  /* 모바일 반응형 */
  @media (max-width: 768px) {
    .modal-overlay {
      padding: 0.5rem;
    }

    .deadline-modal {
      max-width: 100%;
      margin: 0;
    }

    .modal-header {
      padding: 1rem;
    }

    .modal-body {
      padding: 1rem;
    }

    .modal-footer {
      padding: 1rem;
      flex-direction: column-reverse;
    }

    .modal-footer .btn {
      width: 100%;
      min-width: auto;
    }

    .modal-header h3 {
      font-size: 1.1rem;
    }
  }
</style>

 